/*
  PREVIEW FIX — what was going on
  -------------------------------
  "Preview failed — SyntaxError: Importing binding name 'activeAnimations' is not found"
  is NOT a bug in the app code. It is the sandbox's ESM resolver choking on `framer-motion`:
  some CDN/pre-bundled builds of framer-motion split their internals into chunks, and one of
  those chunks fails to re-export `activeAnimations`, so the whole module graph throws before
  a single line of our code executes. (Recharts can hit the same class of ESM-interop fault.)

  The fix: this single-file build now has ZERO runtime dependencies beyond React.
    • framer-motion  →  a tiny in-house motion layer (Web Animations API + CSS), exposed
                        through a cached `motion.*` Proxy so every existing call-site works.
    • recharts       →  hand-rolled, fully themed SVG charts (line + donut + bars).
  All features, curated data, quirky copy, and the mad-scientist design are unchanged.
  Net result: the preview can no longer fail on a third-party ESM resolution fault.
*/
import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from 'react';
import './styles.css';

/* ============================================================
   COFFEE LAB — mad-scientist brew companion (single-file build)
   ============================================================ */

/* ----------------------------- ICONS ----------------------------- */
const Svg = ({ size = 20, sw = 2.1, fill = 'none', children, className = '', style = {} }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    {children}
  </svg>
);
const STAR_PATH = "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";
const Ic = {
  flask: p => <Svg {...p}><path d="M9 3h6" /><path d="M10 3v6L4.4 18.6A2 2 0 0 0 6.2 21.5h11.6a2 2 0 0 0 1.8-2.9L14 9V3" /><path d="M7.5 15h9" /></Svg>,
  timer: p => <Svg {...p}><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5" /><path d="M9 2h6" /></Svg>,
  bean: p => <Svg {...p}><g transform="rotate(40 12 12)"><ellipse cx="12" cy="12" rx="6" ry="9" /><path d="M12 3.5c-3.2 3-3.2 14 0 17" /></g></Svg>,
  book: p => <Svg {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></Svg>,
  chart: p => <Svg {...p}><path d="M3 3v18h18" /><rect x="7" y="12" width="3" height="6" /><rect x="12" y="8" width="3" height="10" /><rect x="17" y="5" width="3" height="13" /></Svg>,
  gear: p => <Svg {...p}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" /></Svg>,
  camera: p => <Svg {...p}><path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" /><circle cx="12" cy="13" r="3.5" /></Svg>,
  upload: p => <Svg {...p}><path d="M12 16V4" /><path d="m6 10 6-6 6 6" /><path d="M4 20h16" /></Svg>,
  download: p => <Svg {...p}><path d="M12 4v12" /><path d="m6 10 6 6 6-6" /><path d="M4 20h16" /></Svg>,
  plus: p => <Svg {...p}><path d="M12 5v14M5 12h14" /></Svg>,
  trash: p => <Svg {...p}><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="M6 7l1 14h10l1-14" /><path d="M10 11v6M14 11v6" /></Svg>,
  play: p => <Svg {...p} fill="currentColor"><path d="M7 4.5v15l13-7.5-13-7.5z" stroke="none" /></Svg>,
  pause: p => <Svg {...p} fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" stroke="none" /><rect x="14" y="4" width="4" height="16" rx="1" stroke="none" /></Svg>,
  skip: p => <Svg {...p} fill="currentColor"><path d="M5 5v14l10-7L5 5z" stroke="none" /><rect x="17" y="5" width="3" height="14" rx="1" stroke="none" /></Svg>,
  x: p => <Svg {...p}><path d="M6 6l12 12M18 6L6 18" /></Svg>,
  star: ({ filled, ...p }) => <Svg {...p} fill={filled ? 'currentColor' : 'none'}><path d={STAR_PATH} /></Svg>,
  chevL: p => <Svg {...p}><path d="m14 6-6 6 6 6" /></Svg>,
  chevD: p => <Svg {...p}><path d="m6 9 6 6 6-6" /></Svg>,
  funnel: p => <Svg {...p}><path d="M3 4h18l-7 9v6l-4 2v-8L3 4z" /></Svg>,
  waves: p => <Svg {...p}><path d="M2 8c2-2.5 4-2.5 6 0s4 2.5 6 0 4-2.5 6 0" /><path d="M2 14c2-2.5 4-2.5 6 0s4 2.5 6 0 4-2.5 6 0" /><path d="M2 20c2-2.5 4-2.5 6 0s4 2.5 6 0 4-2.5 6 0" /></Svg>,
  toggle: p => <Svg {...p}><rect x="2" y="7" width="20" height="10" rx="5" /><circle cx="8" cy="12" r="3" /></Svg>,
  cylinder: p => <Svg {...p}><ellipse cx="12" cy="5" rx="7" ry="2.6" /><path d="M5 5v14c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6V5" /><path d="M12 2v3" /></Svg>,
  droplet: p => <Svg {...p}><path d="M12 2.5s6 6.6 6 11a6 6 0 0 1-12 0c0-4.4 6-11 6-11z" /></Svg>,
  clock: p => <Svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></Svg>,
  stir: p => <Svg {...p}><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></Svg>,
  plunge: p => <Svg {...p}><path d="M12 3v13" /><path d="m6 11 6 6 6-6" /><path d="M5 21h14" /></Svg>,
  alert: p => <Svg {...p}><path d="M12 3 2.5 20h19L12 3z" /><path d="M12 10v4" /><path d="M12 17.5v.1" /></Svg>,
  check: p => <Svg {...p}><path d="m4 12.5 5 5L20 6.5" /></Svg>,
  up: p => <Svg {...p}><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></Svg>,
  down: p => <Svg {...p}><path d="M12 5v14" /><path d="m5 12 7 7 7-7" /></Svg>,
  edit: p => <Svg {...p}><path d="M4 20h4L20 8l-4-4L4 16v4z" /><path d="m13.5 6.5 4 4" /></Svg>,
  copy: p => <Svg {...p}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>,
  sun: p => <Svg {...p}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></Svg>,
  moon: p => <Svg {...p}><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></Svg>,
  monitor: p => <Svg {...p}><rect x="2" y="4" width="20" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></Svg>,
  volume: p => <Svg {...p}><path d="M11 5 6 9H3v6h3l5 4V5z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 5.5a9 9 0 0 1 0 13" /></Svg>,
  trophy: p => <Svg {...p}><path d="M8 21h8M12 17v4" /><path d="M7 4h10v6a5 5 0 0 1-10 0V4z" /><path d="M7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4" /></Svg>,
  weight: p => <Svg {...p}><circle cx="12" cy="6" r="3" /><path d="M6.5 9h11L20 21H4L6.5 9z" /></Svg>,
  thermo: p => <Svg {...p}><path d="M14 4a2 2 0 0 0-4 0v9.5a4 4 0 1 0 4 0V4z" /><path d="M12 9v6" /></Svg>,
};
const METHOD_ICON = { v60: Ic.funnel, kalita: Ic.waves, switch: Ic.toggle, aeropress: Ic.cylinder };
const ACTION_ICON = { bloom: Ic.droplet, pour: Ic.droplet, wait: Ic.clock, stir: Ic.stir, plunge: Ic.plunge };
const ACTION_META = {
  bloom: { label: 'BLOOM', quirky: 'RELEASE THE GASES!', color: '#76FF03' },
  pour: { label: 'POUR', quirky: 'INITIATE FLUID TRANSFER', color: '#4FC3F7' },
  stir: { label: 'STIR', quirky: 'AGITATE THE SPECIMEN', color: '#FFC107' },
  wait: { label: 'WAIT', quirky: 'OBSERVE THE CHEMISTRY', color: '#AA00FF' },
  plunge: { label: 'PLUNGE', quirky: 'APPLY PRESSURE', color: '#FF7043' },
};

/* ----------------------------- CONSTANTS ----------------------------- */
const METHODS = {
  v60: { label: 'V60', sub: 'Cone dripper', color: '#76FF03', defaultFlowRate: 6 },
  kalita: { label: 'Kalita Wave', sub: 'Flat-bed wave', color: '#FFC107', defaultFlowRate: 4 },
  switch: { label: 'Hario Switch', sub: 'Steep + release', color: '#AA00FF', defaultFlowRate: 8 },
  aeropress: { label: 'AeroPress', sub: 'Pressure pod', color: '#FF7043', defaultFlowRate: 5 },
};
/* Plunge steps (AeroPress) get a different default flow rate since they
   represent a press, not a kettle pour. */
const PLUNGE_FLOW_RATE = 3.5;
const ROAST_LEVELS = [
  { name: 'Light', color: '#D9B48F', ink: '#4a2f14' },
  { name: 'Medium-Light', color: '#C08A5A', ink: '#3a2210' },
  { name: 'Medium', color: '#96603A', ink: '#fff' },
  { name: 'Medium-Dark', color: '#6B4226', ink: '#fff' },
  { name: 'Dark', color: '#3E2723', ink: '#F5F5F0' },
];
const FLAVOR_TAGS = ['Fruity', 'Floral', 'Nutty', 'Chocolatey', 'Caramel', 'Spicy', 'Herbal', 'Citrus', 'Berry', 'Stone Fruit', 'Tropical', 'Earthy'];
const PROCESSING_OPTS = ['Washed', 'Natural', 'Honey', 'Anaerobic', 'Other'];
const STEP_KINDS = ['pour', 'wait', 'stir', 'plunge'];

const COPY = {
  bloom: ['Bloom', 'RELEASE THE GASES!'], pour: ['Pour', 'INITIATE FLUID TRANSFER'],
  wait: ['Wait', 'OBSERVE THE CHEMISTRY'], stir: ['Stir', 'AGITATE THE SPECIMEN'],
  plunge: ['Plunge', 'APPLY PRESSURE'], brewComplete: ['Brew complete', 'EUREKA! EXTRACTION SUCCESSFUL!'],
  lowStock: ['Running low', 'CRITICALLY LOW. PANIC ACCORDINGLY.'], emptyBeans: ['No beans yet', 'Your lab is sterile.'],
  emptyRecipes: ['No recipes yet', 'No protocols on file.'], emptyBrews: ['No brews yet', 'Zero experiments recorded. Disappointing.'],
  save: ['Save', 'COMMIT TO ARCHIVE'], del: ['Delete', 'TERMINATE'], addBean: ['Add bean', 'ACQUIRE SPECIMEN'],
  addRecipe: ['Add recipe', 'DESIGN PROTOCOL'], start: ['Start experiment', 'INITIATE EXPERIMENT'],
  skip: ['Skip', 'FAST FORWARD'], abort: ['Cancel', 'ABORT MISSION'], rate: ['How was it?', 'Rate your creation:'],
  export: ['Export data', 'DOWNLOAD LAB NOTES'], import: ['Import data', 'UPLOAD EXTERNAL DATA'],
  settings: ['Settings', 'Lab Configuration'],
};

const step = (id, order, action, description, duration, targetWeight = null, tip = null) =>
  ({ id, order, action, description, duration, targetWeight, tip });
/* Step schema v2 — `kind` replaces `action` and bloom steps are split into
   pour+wait pairs. Valid kinds: 'pour' | 'wait' | 'stir' | 'plunge'.

   - `pour` / `plunge` = volume-bound. `targetWeight` (g) is required.
     `flowRate` (g/s) is informational — used to compute the expected pour
     duration shown on the TimerScreen. `duration` is the user-set total
     step duration in seconds (pour time + bed-settle time before the next
     step). For pour: flowRate default = METHODS[method].defaultFlowRate.
     For plunge: flowRate default = PLUNGE_FLOW_RATE.
   - `wait` / `stir` = time-bound. Only `duration` (s) is required.
     `targetWeight`, `targetWeightPct`, and `flowRate` are all null.
   - `targetWeightPct` = `targetWeight / waterAmount` (4dp), derived at
     save time. Recipes stay scale-invariant: at brew time, the step's
     grams for the user's actual water is `water × targetWeightPct`. */
const derivePcts = (steps, waterAmount, method = null) => {
  const w = Number(waterAmount) || 0;
  const methodDefault = (method && METHODS[method]?.defaultFlowRate) || 6;
  return (steps || []).map(s => {
    const kind = s.kind;
    let out = s;
    // targetWeightPct: only meaningful for pour/plunge.
    const hasWeight = kind === 'pour' || kind === 'plunge';
    if (hasWeight && s.targetWeight != null && w > 0) {
      out = { ...out, targetWeightPct: Math.round((s.targetWeight / w) * 10000) / 10000 };
    } else {
      out = { ...out, targetWeightPct: null };
    }
    // flowRate: back-fill on pour/plunge if missing.
    if (hasWeight && out.flowRate == null) {
      out = { ...out, flowRate: kind === 'plunge' ? PLUNGE_FLOW_RATE : methodDefault };
    } else if (!hasWeight) {
      out = { ...out, flowRate: null };
    }
    return out;
  });
};
/* One-shot migration: schema v1 (action) → v2 (kind), with bloom steps split
   into pour + wait pairs. Idempotent — runs every load but is a no-op once
   `kind` is present on every step.

   What changes:
   - `action: 'bloom'` with both targetWeight and duration → split into TWO
     steps: a pour (targetWeight, flowRate, no duration-derived yet) and a
     wait (duration = original bloom's duration, no target weight). The pour
     step's duration is set to the original bloom's duration as well — bloom
     is "pour this much, then wait this long", and the original `duration`
     field was always the total time the step occupies in the brew. The new
     pour step's `duration` is the *full* step duration; the TimerScreen
     shows the user the pour-time (derived from flowRate) vs. the total
     time remaining so the wait-after-pour is visible.
   - `action: 'stir' | 'wait' | 'plunge' | 'pour'` → renamed to `kind`.
   - Each step gets its `targetWeightPct` and (if applicable) `flowRate`
     back-filled via `derivePcts`.
   - All `order` values re-numbered 0..N so the array is dense after the
     bloom split. */
const migrateRecipes = recipes => (recipes || []).map(r => {
  const steps = r.steps || [];
  // Already on v2 schema? No-op.
  if (steps.every(s => 'kind' in s)) return r;

  const methodDefault = METHODS[r.method]?.defaultFlowRate || 6;
  const water = Number(r.waterAmount) || 0;
  const newSteps = [];
  for (const s of steps) {
    const action = s.action;
    if (action === 'bloom' && s.targetWeight && Number(s.duration) > 0) {
      /* Split bloom into (pour to target) + (wait for bed to settle).
         The pour step's `duration` is the expected pour time
         (targetWeight / defaultFlowRate, in seconds, rounded). The wait
         step gets the *remaining* time. The total step time is preserved
         (pour + wait == original duration), so recipe total durations don't
         change from the user's perspective.

         Edge case: pour time exceeds original duration (very low flow rate
         or very heavy bloom). In that case the pour uses the full original
         duration and the wait is 0 — the TimerScreen's countdown is honest
         about the time the pour actually needs. */
      const totalDur = Number(s.duration);
      const pourDur = Math.min(totalDur, Math.round((Number(s.targetWeight) || 0) / methodDefault));
      const waitDur = Math.max(0, totalDur - pourDur);
      newSteps.push({
        ...s,
        kind: 'pour',
        action: undefined,
        duration: pourDur,
        flowRate: methodDefault,
        // targetWeightPct will be set by derivePcts below.
      });
      if (waitDur > 0) {
        newSteps.push({
          ...s,
          id: s.id + '-w', // disambiguate from the pour step
          kind: 'wait',
          action: undefined,
          targetWeight: null,
          targetWeightPct: null,
          flowRate: null,
          description: 'Let the bed settle.',
          tip: null,
          duration: waitDur,
        });
      }
    } else {
      // Plain rename + drop the legacy `action` field.
      const kind = (action === 'stir' || action === 'wait' || action === 'pour' || action === 'plunge') ? action : 'wait';
      newSteps.push({ ...s, kind, action: undefined });
    }
  }
  // Re-number orders 0..N (dense), then derive pcts/flowRate.
  const renumbered = newSteps.map((s, i) => ({ ...s, order: i }));
  return { ...r, steps: derivePcts(renumbered, r.waterAmount, r.method) };
});

const CURATED = [
  { id: 'cur-1', name: 'The Classic', method: 'v60', isCurated: true, coffeeDose: 15, waterAmount: 250, grindSize: 'Medium-Fine', temperature: 96,
    description: 'Classic pour-over with bloom. The house standard.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c1s1', 0, 'bloom', 'Pour 50g to bloom. Swirl gently.', 30, 50, 'Keep the stream thin and steady.'),
      step('c1s2', 1, 'pour', 'Pour to 150g in slow circles.', 45, 150),
      step('c1s3', 2, 'pour', 'Pour to 250g. Keep steady.', 30, 250),
      step('c1s4', 3, 'wait', 'Let it draw down. Resist the urge to stir.', 45), ] },
  { id: 'cur-2', name: "Beginner's Luck", method: 'v60', isCurated: true, coffeeDose: 20, waterAmount: 300, grindSize: 'Medium', temperature: 94,
    description: 'A simpler two-pour routine. Hard to mess up. Probably.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c2s1', 0, 'bloom', 'Pour 60g. Wait for the bubbles.', 45, 60),
      step('c2s2', 1, 'pour', 'Pour the rest to 300g.', 45, 300),
      step('c2s3', 2, 'wait', 'Patience, young scientist.', 90), ] },
  { id: 'cur-3', name: 'Wave Rider', method: 'kalita', isCurated: true, coffeeDose: 18, waterAmount: 300, grindSize: 'Medium', temperature: 95,
    description: 'Four-pour kalita cadence for a flat, forgiving bed.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c3s1', 0, 'bloom', 'Pour 40g. Watch the bed rise.', 30, 40),
      step('c3s2', 1, 'pour', 'Pour to 150g.', 30, 150),
      step('c3s3', 2, 'pour', 'Pour to 225g.', 30, 225),
      step('c3s4', 3, 'pour', 'Pour to 300g.', 30, 300),
      step('c3s5', 4, 'wait', 'Flat bed = happy bed.', 60, null, 'Level the slurry with a gentle shake if needed.') ] },
  { id: 'cur-4', name: 'The Switcheroo', method: 'switch', isCurated: true, coffeeDose: 15, waterAmount: 250, grindSize: 'Medium-Coarse', temperature: 96,
    description: 'Closed-valve steep, then the big release.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c4s1', 0, 'bloom', 'Switch CLOSED. Pour 50g.', 30, 50),
      step('c4s2', 1, 'wait', 'Let it steep. The science is happening.', 90),
      step('c4s3', 2, 'stir', 'Gentle stir. Wake it up.', 15),
      step('c4s4', 3, 'plunge', 'OPEN THE SWITCH! Let it flow.', 15, null, 'Flip the valve decisively. No hesitation.'),
      step('c4s5', 4, 'wait', 'Draw down complete.', 60) ] },
  { id: 'cur-5', name: 'Standard Issue', method: 'aeropress', isCurated: true, coffeeDose: 15, waterAmount: 240, grindSize: 'Medium-Fine', temperature: 85,
    description: 'The reliable workhorse press. 85°C keeps it smooth.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c5s1', 0, 'bloom', 'Pour 50g. Quick wetting.', 10, 50),
      step('c5s2', 1, 'pour', 'Pour to 240g.', 20, 240),
      step('c5s3', 2, 'stir', '3 gentle stirs back and forth.', 10),
      step('c5s4', 3, 'wait', 'Cap on. Wait. Pressure is building.', 50),
      step('c5s5', 4, 'plunge', 'Press slowly. 20–30 seconds.', 15, null, 'Stop at the hiss. Or don’t. Chaos is legal.') ] },
  { id: 'cur-6', name: 'Upside Down', method: 'aeropress', isCurated: true, coffeeDose: 17, waterAmount: 220, grindSize: 'Medium', temperature: 90,
    description: 'Inverted method. Full immersion, dramatic flip.', createdAt: '2026-01-01T08:00:00Z', steps: [
      step('c6s1', 0, 'bloom', 'Inverted. Pour 50g.', 30, 50),
      step('c6s2', 1, 'pour', 'Pour to 220g.', 30, 220),
      step('c6s3', 2, 'stir', '2 stirs. Don’t get cocky.', 10),
      step('c6s4', 3, 'wait', 'Steep time. Flip at your own risk.', 50),
      step('c6s5', 4, 'plunge', 'Flip and press. You’ve got this.', 20, null, 'Firm grip. Confident face. Lab coat optional.') ] },
];

/* ----------------------------- UTILS ----------------------------- */
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const fmtTime = s => { s = Math.max(0, Math.round(s)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const recipeTotal = r => (r.steps || []).reduce((a, s) => a + (Number(s.duration) || 0), 0);
const timeAgo = iso => {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now'; if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`; if (d < 604800) return `${Math.floor(d / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const dayLabel = iso => { const d = new Date(iso); return `${d.getMonth() + 1}/${d.getDate()}`; };
const download = (filename, text, mime = 'text/plain') => {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 600);
};
const csvCell = v => { v = v == null ? '' : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
const greeting = quirky => {
  const h = new Date().getHours();
  if (quirky) {
    if (h >= 5 && h <= 11) return 'Good morning, caffeine criminal.';
    if (h >= 12 && h <= 16) return 'Afternoon brew session initiated.';
    if (h >= 17 && h <= 21) return 'Evening experiments? Bold.';
    return 'Burning the midnight oil... or beans.';
  }
  if (h >= 5 && h <= 11) return 'Good morning.'; if (h >= 12 && h <= 16) return 'Good afternoon.';
  if (h >= 17 && h <= 21) return 'Good evening.'; return 'Late-night session.';
};
const stockStatus = b => b.currentWeight > 100 ? 'ok' : b.currentWeight >= 50 ? 'mid' : 'low';

/* ----------------------------- FRESHNESS ----------------------------- */
/* Six-stage freshness model (resting → ready → prime → late-prime → fading → past best).
   Spec by Shawn, derived from a synthesis of:
     - Warfield's Coffee "7-10 Day Rule" (generalwarfieldscoffee.com)
     - CoffeeTec "How Long to Use Coffee Beans" (coffeetec.com)
     - Intercontinental Coffee Trading "Resting Period After Roasting" (ictcoffee.com)
     - Cafe Kreyol post-roast rest study (2022, 21 coffees, double-blind cupping)
     - Scott Rao "Resting Roasts" (scottrao.com)
   No public source quantifies (roast × processing) interactions; the processing
   modifier here is a small directional adjustment (washed holds slightly longer
   than natural/anaerobic) consistent with the sources' qualitative claims. */
/* Stage order. Index 0 = freshest. */
const FRESHNESS_STAGES = ['resting', 'ready', 'prime', 'latePrime', 'fading', 'pastBest'];
/* Days (since roast) for each stage transition.
   Index = end-of-stage days. So row[0] = end of resting, row[1] = end of ready, etc. */
const FRESHNESS_TABLE = {
  /* Bucket: { restEnd, readyEnd, primeEnd, latePrimeEnd, fadingEnd } (fadingEnd = pastBest start) */
  //                                    restEnd  readyEnd primeEnd  latePrimeEnd  fadingEnd
  'light|washed':     [6,  10, 24, 32, 42],
  'light|natural':    [8,  13, 28, 38, 49],   // natural / anaerobic / "highly processed"
  'light|other':      [8,  13, 28, 38, 49],
  'lightMedium|any':  [5,  9,  21, 28, 38],
  'medium|any':       [3,  7,  17, 24, 32],
};
/* Visual config per stage. color names map to CSS classes (.fresh-dot-{color}). */
const FRESHNESS_META = {
  resting:    { color: 'blue',   label: 'RESTING',     hint: 'Degassing — let it rest a few more days.' },
  ready:      { color: 'cyan',   label: 'READY',       hint: 'Drinkable, but flavor is still developing.' },
  prime:      { color: 'green',  label: 'PRIME',       hint: 'In the sweet spot — brew now.' },
  latePrime:  { color: 'lime',   label: 'LATE-PRIME',  hint: 'Still good. Drink soon.' },
  fading:     { color: 'yellow', label: 'FADING',      hint: 'Aroma and clarity are dropping off.' },
  pastBest:   { color: 'red',    label: 'PAST BEST 💀', hint: 'Stale. Use for cold brew or baking.' },
};
const roastBucket = level => {
  if (level === 'Light') return 'light';
  if (level === 'Medium-Light') return 'lightMedium';
  return 'medium';
};
const processBucket = method => {
  // Washed/clean → washed bucket. Honey bucketed with washed (slower degassing
  // than natural per the sources). Natural, Anaerobic, and unknown/other
  // group together as "highly processed" — faster degas + faster fade.
  if (method === 'Washed') return 'washed';
  if (method === 'Honey') return 'washed';
  if (method === 'Natural' || method === 'Anaerobic') return 'natural';
  return 'other';
};
const daysSinceRoast = iso => {
  if (!iso) return null;
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  return Math.max(0, Math.floor(d));
};
const getFreshness = bean => {
  const days = daysSinceRoast(bean.roastDate);
  if (days === null) {
    return { stage: 'unknown', daysSince: null, label: 'AGE UNKNOWN', color: 'muted', hint: 'Set a roast date to see freshness.' };
  }
  // Future-dated roast (e.g. typo): treat as just-roasted.
  if (bean.roastDate && new Date(bean.roastDate).getTime() > Date.now()) {
    return { stage: 'notRoasted', daysSince: 0, label: 'NOT YET ROASTED', color: 'muted', hint: 'Roast date is in the future.' };
  }
  // Unknown roast level or processing → fall back to light + washed (the
  // most conservative / longest window, per the spec). For known roast
  // levels, fall back to the "any processing" row for that roast bucket —
  // the user spec says medium/medium-dark/dark don't differentiate by
  // processing, so we use the "any" row directly.
  const rBucket = bean.roastLevel ? roastBucket(bean.roastLevel) : 'light';
  const pBucket = bean.processingMethod ? processBucket(bean.processingMethod) : 'washed';
  const key = `${rBucket}|${pBucket}`;
  let table = FRESHNESS_TABLE[key];
  if (!table && rBucket !== 'light') table = FRESHNESS_TABLE[`${rBucket}|any`];
  if (!table) table = FRESHNESS_TABLE['light|washed'];
  let stageIdx = 0;
  if (days >= table[0]) stageIdx = 1;
  if (days >= table[1]) stageIdx = 2;
  if (days >= table[2]) stageIdx = 3;
  if (days >= table[3]) stageIdx = 4;
  if (days >= table[4]) stageIdx = 5;
  const stage = FRESHNESS_STAGES[stageIdx];
  const meta = FRESHNESS_META[stage];
  return { stage, daysSince: days, label: meta.label, color: meta.color, hint: meta.hint };
};

/* ----------------------------- AUDIO ----------------------------- */
const soundState = { on: true, vol: 0.7 };
let actx = null;
const audioCtx = () => {
  try {
    if (!actx) actx = new (window.AudioContext || window.webkitAudioContext)();
    if (actx.state === 'suspended') actx.resume();
    return actx;
  } catch (e) { return null; }
};
function playBeep(frequency = 800, duration = 0.15, type = 'sine') {
  if (!soundState.on) return;
  const ctx = audioCtx(); if (!ctx) return;
  try {
    const osc = ctx.createOscillator(), gain = ctx.createGain();
    osc.type = type; osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(Math.max(0.0001, 0.14 * soundState.vol), ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.connect(gain); gain.connect(ctx.destination); osc.start(); osc.stop(ctx.currentTime + duration);
  } catch (e) { /* silent */ }
}
const playStepBeep = () => { playBeep(600, 0.1); setTimeout(() => playBeep(800, 0.1), 120); };
const playEureka = () => [261.63, 329.63, 392.0, 523.25].forEach((f, i) => setTimeout(() => playBeep(f, 0.3, 'triangle'), i * 150));
const playClick = () => playBeep(400, 0.05, 'square');

/* ----------------------------- STORE ----------------------------- */
const DATA_KEY = 'coffeelab.data.v1';
const SET_KEY = 'coffeelab.settings.v1';
const seedData = () => ({ beans: [], recipes: migrateRecipes(CURATED.map(r => JSON.parse(JSON.stringify(r)))), brews: [] });
const loadData = () => {
  try { const raw = localStorage.getItem(DATA_KEY); if (raw) { const d = JSON.parse(raw); if (d && Array.isArray(d.recipes)) return { ...d, recipes: migrateRecipes(d.recipes) }; } } catch (e) { /* */ }
  return seedData();
};
const loadSettings = () => {
  const def = { theme: 'dark', quirky: true, sound: true, volume: 70 };
  try { const raw = localStorage.getItem(SET_KEY); if (raw) return { ...def, ...JSON.parse(raw) }; } catch (e) { /* */ }
  return def;
};
const AppCtx = createContext(null);
const useApp = () => useContext(AppCtx);

/* ============================================================
   MOTION LAYER  (replaces framer-motion — no external dep)
   - Web Animations API for enter transitions (initial/animate/variants)
   - CSS for hover/tap interaction states (whileHover/whileTap)
   - Cached Proxy so `motion.div` keeps a stable component identity
   ============================================================ */
const toTf = o => {
  if (!o) return '';
  const p = [];
  if (o.x != null || o.y != null) p.push(`translate(${o.x || 0}px, ${o.y || 0}px)`);
  if (o.scale != null) p.push(`scale(${o.scale})`);
  if (o.rotate != null) p.push(`rotate(${o.rotate}deg)`);
  return p.join(' ');
};
const toStyleObj = o => {
  if (!o) return {};
  const s = {};
  if (o.opacity != null) s.opacity = o.opacity;
  const tf = toTf(o); if (tf) s.transform = tf;
  return s;
};
function Motion({ as: Tag = 'div', initial, animate, exit, transition, whileHover, whileTap,
  variants, custom, layoutId, layout, className = '', style = {}, children, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el || !el.animate) return;
    let fromO = initial, toO = animate, tr = transition || {};
    if (variants) {
      const fi = typeof variants[initial] === 'function' ? variants[initial](custom) : variants[initial];
      const ta = typeof variants[animate] === 'function' ? variants[animate](custom) : variants[animate];
      fromO = fi; toO = ta; tr = (ta && ta.transition) || tr;
    }
    if (!fromO || !toO) return;
    const dur = (tr.duration != null ? tr.duration : 0.4) * 1000;
    const delay = (tr.delay != null ? tr.delay : 0) * 1000;
    const easing = tr.type === 'spring' ? 'cubic-bezier(.34,1.56,.64,1)' : 'cubic-bezier(.22,.61,.36,1)';
    try { el.animate([toStyleObj(fromO), toStyleObj(toO)], { duration: dur, delay, easing, fill: 'backwards' }); } catch (e) { /* */ }
  }, []);
  const cssVars = {};
  if (whileHover) cssVars['--hover-tf'] = toTf(whileHover);
  if (whileTap) cssVars['--tap-tf'] = toTf(whileTap);
  let cls = className;
  if (whileHover) cls += ' fx-hover';
  if (whileTap) cls += ' fx-tap';
  if (layoutId) cls += ' fx-pop';
  return <Tag ref={ref} className={cls} style={{ ...style, ...cssVars }} {...rest}>{children}</Tag>;
}
const motionCache = new Map();
const motion = new Proxy({}, {
  get(_, tag) {
    if (typeof tag !== 'string') return undefined;
    if (!motionCache.has(tag)) motionCache.set(tag, (props) => <Motion as={tag} {...props} />);
    return motionCache.get(tag);
  },
});
const AnimatePresence = ({ children }) => <>{children}</>;

/* Overlay: modal/sheet/confirm wrapper with proper enter + exit (WAAPI) */
function Overlay({ open, onBackdrop, panelClass, panelAnim = 'scale', backdropClass = '', children }) {
  const [mounted, setMounted] = useState(open);
  const backRef = useRef(null), panelRef = useRef(null);
  useEffect(() => {
    if (open) { setMounted(true); return; }
    if (!mounted) return;
    const b = backRef.current, p = panelRef.current;
    if (b && b.animate) b.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, fill: 'forwards' });
    if (p && p.animate) {
      const out = panelAnim === 'sheet'
        ? [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(120px)', opacity: 0 }]
        : [{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(.92)', opacity: 0 }];
      p.animate(out, { duration: 200, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' });
    }
    const t = setTimeout(() => setMounted(false), 210);
    return () => clearTimeout(t);
  }, [open]);
  useEffect(() => {
    if (!(open && mounted)) return;
    const p = panelRef.current, b = backRef.current;
    if (p && p.animate) {
      const inn = panelAnim === 'sheet'
        ? [{ transform: 'translateY(120px)', opacity: .6 }, { transform: 'translateY(0)', opacity: 1 }]
        : [{ transform: 'scale(.9) translateY(24px)', opacity: 0 }, { transform: 'scale(1) translateY(0)', opacity: 1 }];
      p.animate(inn, { duration: 320, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' });
    }
    if (b && b.animate) b.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, fill: 'backwards' });
  }, [open, mounted]);
  if (!mounted) return null;
  return (
    <div ref={backRef} className={'modal-back ' + backdropClass} onClick={onBackdrop || undefined}>
      <div ref={panelRef} className={panelClass} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

/* ----------------------------- CHARTS  (replaces recharts) ----------------------------- */
function useWidth(init = 320) {
  const ref = useRef(null); const [w, setW] = useState(init);
  useEffect(() => {
    const el = ref.current; if (!el || !window.ResizeObserver) return;
    const ro = new ResizeObserver(es => { for (const e of es) setW(Math.max(40, e.contentRect.width)); });
    ro.observe(el); return () => ro.disconnect();
  }, []);
  return [ref, w];
}
const LineChartSVG = ({ data, c, height = 190 }) => {
  const [ref, w] = useWidth(320); const [hi, setHi] = useState(null);
  const l = 34, r = 12, t = 14, b = 24, n = data.length;
  const pw = Math.max(10, w - l - r), ph = height - t - b;
  const X = i => n <= 1 ? l + pw / 2 : l + (i / (n - 1)) * pw;
  const Y = v => t + (1 - v / 5) * ph;
  const path = data.map((d, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(d.v).toFixed(1)}`).join(' ');
  const area = `${path} L${X(n - 1).toFixed(1)} ${(t + ph).toFixed(1)} L${X(0).toFixed(1)} ${(t + ph).toFixed(1)} Z`;
  const onMove = e => { const mx = e.nativeEvent.offsetX; setHi(clamp(Math.round(((mx - l) / pw) * (n - 1)), 0, n - 1)); };
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} style={{ display: 'block', overflow: 'visible' }}>
        <defs><linearGradient id="lnArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c.line} stopOpacity="0.3" /><stop offset="100%" stopColor={c.line} stopOpacity="0" />
        </linearGradient></defs>
        {[1, 2, 3, 4, 5].map(v => <line key={v} x1={l} x2={w - r} y1={Y(v)} y2={Y(v)} stroke={c.grid} strokeDasharray="3 3" />)}
        {[1, 2, 3, 4, 5].map(v => <text key={'t' + v} x={l - 7} y={Y(v) + 3} textAnchor="end" fontSize="9" fontFamily="'Space Mono',monospace" fill={c.axis}>{v}</text>)}
        {data.map((d, i) => (n <= 8 || i % 2 === 0) && <text key={'x' + i} x={X(i)} y={height - 7} textAnchor="middle" fontSize="9" fontFamily="'Space Mono',monospace" fill={c.axis}>{d.d}</text>)}
        <path d={area} fill="url(#lnArea)" />
        <path d={path} fill="none" stroke={c.line} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {data.map((d, i) => <circle key={'d' + i} cx={X(i)} cy={Y(d.v)} r={hi === i ? 6 : 3.5} fill={c.dot} />)}
        {hi != null && <>
          <line x1={X(hi)} x2={X(hi)} y1={t} y2={t + ph} stroke={c.line} strokeOpacity="0.4" strokeDasharray="2 3" />
          <g transform={`translate(${clamp(X(hi), l + 32, w - r - 32)}, ${Math.max(Y(data[hi].v) - 36, t)})`}>
            <rect x={-30} y={-12} width={60} height={26} rx={6} fill={c.tipBg} stroke={c.tipBd} strokeWidth="1.5" />
            <text x={0} y={-1} textAnchor="middle" fontSize="9" fontFamily="'Space Mono',monospace" fill={c.axis}>{data[hi].d}</text>
            <text x={0} y={10} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="'Space Mono',monospace" fill={c.line}>{data[hi].v.toFixed(1)}★</text>
          </g>
        </>}
        <rect x={0} y={0} width={w} height={height} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHi(null)} style={{ cursor: 'crosshair' }} />
      </svg>
    </div>
  );
};
const DonutSVG = ({ data, c, total }) => {
  const R = 58, C = 2 * Math.PI * R, cx = 90, cy = 90, th = 22; let acc = 0;
  return (
    <svg viewBox="0 0 180 180" width="172" height="172" style={{ display: 'block', margin: '0 auto' }}>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke={c.track} strokeWidth={th} />
      {data.map((d, i) => { const len = total ? (d.value / total) * C : 0; const dash = Math.max(0, len - 2);
        const el = <circle key={i} cx={cx} cy={cy} r={R} fill="none" stroke={d.color} strokeWidth={th}
          strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc} transform={`rotate(-90 ${cx} ${cy})`} />; acc += len; return el; })}
      <text x={cx} y={cy - 1} textAnchor="middle" fontSize="27" fontFamily="'Bungee',sans-serif" fill={c.ink}>{total}</text>
      <text x={cx} y={cy + 15} textAnchor="middle" fontSize="8.5" letterSpacing="1.5" fontFamily="'Space Mono',monospace" fill={c.axis}>BREWS</text>
    </svg>
  );
};
const BarChartSVG = ({ data, c, height = 170 }) => {
  const [ref, w] = useWidth(320);
  const l = 30, r = 8, t = 14, b = 22, pw = Math.max(10, w - l - r), ph = height - t - b;
  const max = Math.max(1, ...data.map(d => d.g));
  const stepW = pw / data.length, bw = stepW * 0.58;
  const Y = v => t + (1 - v / max) * ph;
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} style={{ display: 'block' }}>
        {[0, 0.5, 1].map((f, i) => { const v = Math.round(max * f), y = Y(v); return (
          <g key={i}><line x1={l} x2={w - r} y1={y} y2={y} stroke={c.grid} strokeDasharray="3 3" />
            <text x={l - 6} y={y + 3} textAnchor="end" fontSize="9" fontFamily="'Space Mono',monospace" fill={c.axis}>{v}</text></g>); })}
        {data.map((d, i) => { const x = l + i * stepW + (stepW - bw) / 2, y = Y(d.g), hgt = (t + ph) - y; return (
          <g key={i}><rect x={x} y={y} width={bw} height={Math.max(0, hgt)} rx={4} fill={c.bar} />
            <text x={x + bw / 2} y={height - 7} textAnchor="middle" fontSize="9" fontFamily="'Space Mono',monospace" fill={c.axis}>{d.d}</text>
            {d.g > 0 && <text x={x + bw / 2} y={y - 4} textAnchor="middle" fontSize="9" fontWeight="700" fontFamily="'Space Mono',monospace" fill={c.ink}>{d.g}g</text>}</g>); })}
      </svg>
    </div>
  );
};

/* ----------------------------- SMALL UI ----------------------------- */
const Stars = ({ value, size = 13 }) => {
  const pct = clamp(value || 0, 0, 5) / 5 * 100;
  return (
    <span className="stars-wrap" style={{ fontSize: size }} aria-label={`${(value || 0).toFixed(1)} of 5`}>
      <span className="stars-bg">★★★★★</span>
      <span className="stars-fg" style={{ width: pct + '%' }}>★★★★★</span>
    </span>
  );
};
const RatingInput = ({ value, onChange }) => (
  <div className="rate-row">
    {[1, 2, 3, 4, 5].map(n => (
      <motion.button key={n} whileTap={{ scale: 1.35 }} type="button"
        className={'rate-star' + (n <= value ? ' on' : '')} onClick={() => { onChange(n); playClick(); }}>
        <Ic.star size={34} filled={n <= value} />
      </motion.button>
    ))}
  </div>
);
const Chip = ({ active, onClick, children, className = '' }) => (
  <motion.button whileTap={{ scale: 0.93 }} type="button"
    className={'chip' + (active ? ' chip-on' : '') + ' ' + className} onClick={onClick}>{children}</motion.button>
);
const Toggle = ({ on, onChange }) => (
  <motion.button whileTap={{ scale: 0.92 }} type="button" className={'tgl' + (on ? ' tgl-on' : '')}
    onClick={() => { onChange(!on); playClick(); }} aria-pressed={on}>
    <span className="tgl-knob" />
  </motion.button>
);
const Field = ({ label, children, hint }) => (
  <label className="field">
    <span className="field-label">{label}{hint && <em className="field-hint">{hint}</em>}</span>
    {children}
  </label>
);
const Stamp = ({ kind, children }) => <span className={'stamp stamp-' + kind}>{children}</span>;
const RoastBadge = ({ level }) => {
  const r = ROAST_LEVELS.find(x => x.name === level) || ROAST_LEVELS[2];
  return <span className="roast-badge" style={{ background: r.color, color: r.ink }}>{level}</span>;
};
const StockDot = ({ status }) => <span className={'stock-dot stock-' + status} title={status === 'ok' ? '>100g' : status === 'mid' ? '50–100g' : '<50g'} />;
/* FreshnessBadge: replaced by a unified `Stat` component (below) used for
   roast, weight, and freshness. The compact variant (no label) still uses
   a small dot-only pill for tight rows like champion card and leaderboard. */
const Stat = ({ color, label, hint, dotGlow = true, children }) => (
  <span className={'stat stat-' + color} title={hint}>
    <span className={'stat-dot stat-dot-' + color + (dotGlow ? ' stat-dot-glow' : '')} />
    {label && <span className="stat-label f-mono">{label}</span>}
    {children}
  </span>
);
const RoastStat = ({ bean, dotGlow = false }) => {
  const level = bean?.roastLevel || 'Unknown';
  const color = level === 'Light' ? 'roast-light'
    : level === 'Medium-Light' ? 'roast-med-light'
    : level === 'Medium' ? 'roast-med'
    : level === 'Medium-Dark' ? 'roast-med-dark'
    : level === 'Dark' ? 'roast-dark'
    : 'muted';
  return <Stat color={color} label={level.toUpperCase()} hint={`Roast level: ${level}`} dotGlow={dotGlow} />;
};
const WeightStat = ({ bean }) => {
  const w = bean?.currentWeight;
  if (w == null) return <Stat color="muted" label="—" hint="No weight recorded" dotGlow={false} />;
  const status = stockStatus(bean);
  const color = status === 'ok' ? 'weight-ok' : status === 'mid' ? 'weight-mid' : 'weight-low';
  const hint = status === 'ok' ? `Plenty left: ${w}g`
    : status === 'mid' ? `Running low: ${w}g remaining`
    : `Almost out: ${w}g left`;
  return <Stat color={color} label={`${w}G`} hint={hint} dotGlow={false} />;
};
const FreshnessStat = ({ bean, compact = false }) => {
  const f = getFreshness(bean);
  const dayHint = f.daysSince != null ? ` · ${f.daysSince}d since roast` : '';
  const fullHint = f.hint + dayHint;
  if (compact) return <Stat color={f.color} hint={fullHint} dotGlow={f.color === 'green' || f.color === 'red'} />;
  return <Stat color={f.color} label={f.label} hint={fullHint} dotGlow={f.color === 'green' || f.color === 'red'} />;
};
/* Backwards-compat alias so existing call sites still work. */
const FreshnessBadge = ({ bean, compact = false }) => <FreshnessStat bean={bean} compact={compact} />;

const EmptyBeaker = () => (
  <svg viewBox="0 0 120 120" className="empty-beaker" aria-hidden="true">
    <path d="M45 14h30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" />
    <path d="M50 14v26L28 92a8 8 0 0 0 7 12h50a8 8 0 0 0 7-12L70 40V14" stroke="currentColor" strokeWidth="4" fill="none" strokeLinejoin="round" />
    <path d="M38 74h44" stroke="currentColor" strokeWidth="3" strokeLinecap="round" opacity=".5" fill="none" />
    <circle cx="52" cy="88" r="4" fill="currentColor" opacity=".55" className="bub b1" />
    <circle cx="66" cy="92" r="3" fill="currentColor" opacity=".45" className="bub b2" />
    <circle cx="60" cy="84" r="2.4" fill="currentColor" opacity=".6" className="bub b3" />
    <text x="94" y="30" fontSize="20" className="mini-bean">☕</text>
  </svg>
);

const Confirm = ({ open, title, body, confirmLabel = 'CONFIRM', danger, onYes, onNo }) => (
  <Overlay open={open} onBackdrop={onNo} panelClass="confirm-box" panelAnim="scale">
    <div className="confirm-icon" style={{ color: danger ? 'var(--danger)' : 'var(--plasma-ink)' }}><Ic.alert size={30} /></div>
    <h3 className="f-disp confirm-title">{title}</h3>
    <p className="confirm-body">{body}</p>
    <div className="confirm-actions">
      <button className="btn" onClick={onNo}>Stand down</button>
      <motion.button whileTap={{ scale: 0.94 }} className={'btn ' + (danger ? 'btn-danger' : 'btn-plasma')} onClick={onYes}>{confirmLabel}</motion.button>
    </div>
  </Overlay>
);

const Sheet = ({ open, onClose, title, tag, children }) => (
  <Overlay open={open} onBackdrop={onClose} panelClass="sheet" panelAnim="sheet">
    <div className="sheet-head">
      <div>{tag && <div className="sec-tag">{tag}</div>}<h3 className="f-disp sheet-title">{title}</h3></div>
      <button className="icon-btn" onClick={onClose} aria-label="Close"><Ic.x size={18} /></button>
    </div>
    <div className="sheet-body">{children}</div>
  </Overlay>
);

const Toast = ({ t, onDone }) => {
  const [leaving, setLeaving] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const a = setTimeout(() => setLeaving(true), Math.max(0, t.ttl - 250));
    const b = setTimeout(onDone, t.ttl);
    return () => { clearTimeout(a); clearTimeout(b); };
  }, []);
  useEffect(() => {
    if (leaving && ref.current && ref.current.animate)
      ref.current.animate([{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-18px)', opacity: 0 }], { duration: 240, fill: 'forwards' });
  }, [leaving]);
  return (
    <motion.div ref={ref} className={'toast toast-' + t.kind}
      initial={{ y: -28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.3 }}>
      {t.kind === 'warn' ? <Ic.alert size={15} /> : <Ic.check size={15} />}<span>{t.msg}</span>
    </motion.div>
  );
};
const ToastStack = () => {
  const { toasts, setToasts } = useApp();
  const remove = id => setToasts(t => t.filter(x => x.id !== id));
  return <div className="toast-stack">{toasts.map(t => <Toast key={t.id} t={t} onDone={() => remove(t.id)} />)}</div>;
};

const ConfettiBurst = () => {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current, ctx = cv.getContext('2d');
    cv.width = window.innerWidth; cv.height = window.innerHeight;
    const colors = ['#76FF03', '#AA00FF', '#FFC107', '#00E676', '#D7CCC8', '#FF1744'];
    const parts = Array.from({ length: 150 }, () => ({
      x: cv.width / 2, y: cv.height * 0.38, vx: (Math.random() - 0.5) * 15, vy: -Math.random() * 13 - 4,
      g: 0.32 + Math.random() * 0.22, w: 5 + Math.random() * 7, h: 8 + Math.random() * 10,
      rot: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.35, c: colors[Math.floor(Math.random() * colors.length)],
    }));
    let raf; const t0 = performance.now();
    const loop = t => {
      ctx.clearRect(0, 0, cv.width, cv.height); const el = (t - t0) / 1000;
      parts.forEach(p => {
        p.vy += p.g; p.x += p.vx; p.y += p.vy; p.vx *= 0.99; p.rot += p.vr;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, 1 - el / 2.6); ctx.fillStyle = p.c;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h); ctx.restore();
      });
      if (el < 2.7) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop); return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={ref} className="confetti-cv" />;
};

const ProgressRing = ({ progress, state, children }) => {
  const R = 108, C = 2 * Math.PI * R;
  const col = state === 'danger' ? 'var(--danger)' : state === 'warn' ? 'var(--warning)' : 'var(--neon)';
  return (
    <div className="ring-wrap">
      <svg width="252" height="252" viewBox="0 0 252 252" className="ring-svg">
        <circle cx="126" cy="126" r={R} fill="none" stroke="var(--ring-track)" strokeWidth="13" />
        <circle cx="126" cy="126" r={R} fill="none" stroke={col} strokeWidth="13" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - clamp(progress, 0, 1))}
          style={{ transition: 'stroke-dashoffset .12s linear, stroke .3s' }} />
        {[...Array(12)].map((_, i) => { const a = (i / 12) * Math.PI * 2;
          return <circle key={i} cx={126 + Math.cos(a) * 122} cy={126 + Math.sin(a) * 122} r="2" fill="var(--muted)" opacity=".5" />; })}
      </svg>
      <div className="ring-center">{children}</div>
    </div>
  );
};

/* ----------------------------- AMBIENT ----------------------------- */
const Floaters = () => (
  <div className="floaters" aria-hidden="true">
    {[
      { top: '8%', left: '4%', del: 0, dur: 9, I: Ic.bean },
      { top: '18%', left: '88%', del: 1.4, dur: 10, I: Ic.flask },
      { top: '62%', left: '6%', del: 2.2, dur: 8, I: Ic.droplet },
      { top: '74%', left: '90%', del: 0.8, dur: 11, I: Ic.bean },
      { top: '40%', left: '94%', del: 3, dur: 9.5, I: Ic.thermo },
      { top: '86%', left: '40%', del: 1.8, dur: 10.5, I: Ic.flask },
    ].map((f, i) => (
      <div key={i} className="floater" style={{ top: f.top, left: f.left, '--del': f.del + 's', '--dur': f.dur + 's' }}>
        <f.I size={44} sw={1.4} />
      </div>
    ))}
  </div>
);

const InstallFab = () => {
  /* PWA install prompt — proper beforeinstallprompt wiring. Visible app-wide
     so users on any page can install.

     Two-step flow to avoid being misleading:
       1. First tap on iOS / no-prompt Chromium: expands into an inline
          tutorial showing the actual steps. On Chromium-with-prompt: fires
          the native install dialog. Either way, after the user has SEEN
          something useful, the FAB lets them X out (second tap or close
          button on the expanded card).
       2. Second tap on the expanded card → dismiss permanently
          (cl.installDismissed = 1, FAB gone forever).

     On Chromium, if the native install dialog is declined (not accepted),
     we auto-dismiss too — there's no point re-prompting. If accepted, the
     appinstalled event clears the prompt event but the FAB also dismisses. */
  const [installDismissed, setInstallDismissed] = useState(() => localStorage.getItem('cl.installDismissed') === '1');
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  useEffect(() => {
    const onBefore = e => { e.preventDefault(); setInstallPromptEvt(e); };
    window.addEventListener('beforeinstallprompt', onBefore);
    const onInstalled = () => { setInstallPromptEvt(null); localStorage.setItem('cl.installDismissed', '1'); setInstallDismissed(true); };
    window.addEventListener('appinstalled', onInstalled);
    const ua = window.navigator.userAgent;
    const isIosDevice = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
    setIsIOS(isIosDevice && !window.MSStream);
    setIsAndroid(/Android/.test(ua) && !/iPad|iPhone|iPod/.test(ua));
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);
  if (installDismissed || (!installPromptEvt && !isIOS)) return null;
  // Chromium with a real prompt available: single-tap fires the prompt.
  const canPrompt = !!installPromptEvt;
  const handleClose = () => {
    setExpanded(false);
    localStorage.setItem('cl.installDismissed', '1');
    setInstallDismissed(true);
  };
  const handleTap = async () => {
    if (canPrompt) {
      try {
        await Promise.resolve()
          .then(() => installPromptEvt.prompt())
          .then(() => installPromptEvt.userChoice)
          .then(choice => {
            if (choice?.outcome === 'accepted') { setInstallPromptEvt(null); handleClose(); }
            // If declined: also dismiss — there's no recovery via re-prompt.
            else if (choice?.outcome === 'dismissed') { handleClose(); }
          })
          .catch(() => { /* prompt failed; fall through to expand inline */ setExpanded(true); });
      } catch (e) { setExpanded(true); }
    } else {
      // iOS / no-prompt: first tap expands inline tutorial; user reads,
      // then taps "Got it" to dismiss.
      setExpanded(true);
    }
  };
  return (
    <motion.div className={'install-fab' + (expanded ? ' install-fab-expanded' : '')}
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}>
      {!expanded ? (
        <button className="install-fab-pill f-mono" onClick={handleTap} aria-label={canPrompt ? 'Install Coffee Lab' : 'Add Coffee Lab to Home Screen'}>
          <span className="install-fab-icon" aria-hidden="true">📲</span>
          <span className="install-fab-text">{canPrompt ? 'INSTALL APP' : isIOS ? 'ADD TO HOME SCREEN' : 'INSTALL COFFEE LAB'}</span>
        </button>
      ) : (
        <div className="install-fab-card" role="dialog" aria-label="Install Coffee Lab instructions">
          <div className="install-fab-head">
            <span className="install-fab-icon" aria-hidden="true">📲</span>
            <span className="install-fab-title f-disp">{isIOS ? 'INSTALL ON iPHONE' : 'INSTALL AS APP'}</span>
            <button className="install-fab-close" onClick={handleClose} aria-label="Close install instructions">×</button>
          </div>
          <ol className="install-fab-steps f-mono">
            {isIOS ? (
              <>
                <li>Tap the <b>Share</b> button in Safari's toolbar.</li>
                <li>Scroll down and tap <b>Add to Home Screen</b>.</li>
                <li>Tap <b>Add</b> in the top right.</li>
              </>
            ) : isAndroid ? (
              <>
                <li>Tap Chrome's <b>⋮ menu</b> (top right).</li>
                <li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li>
                <li>Confirm <b>Install</b>.</li>
              </>
            ) : (
              <>
                <li>Click the install icon in your browser's address bar.</li>
                <li>Confirm <b>Install</b>.</li>
              </>
            )}
          </ol>
          <button className="btn btn-sm install-fab-cta" onClick={handleClose}>GOT IT — DISMISS</button>
        </div>
      )}
    </motion.div>
  );
};

const BottomNav = () => {
  const { route, nav } = useApp();
  const items = [
    { page: 'dashboard', label: 'Lab', I: Ic.flask }, { page: 'brew', label: 'Brew', I: Ic.timer },
    { page: 'beans', label: 'Beans', I: Ic.bean }, { page: 'recipes', label: 'Protocols', I: Ic.book },
    { page: 'reports', label: 'Reports', I: Ic.chart },
  ];
  return (
    <nav className="bottom-nav"><div className="nav-inner">
      {items.map(it => { const active = route.page === it.page; return (
        <motion.button key={it.page} whileTap={{ scale: 0.88 }} className={'nav-btn' + (active ? ' nav-on' : '')}
          onClick={() => { nav(it.page, {}); playClick(); }}>
          {active && <span className="nav-ind" />}
          <it.I size={21} /><span>{it.label}</span>
        </motion.button>); })}
    </div></nav>
  );
};

const PageHead = ({ tag, title, sub, back, action }) => (
  <header className="page-head">
    <div className="page-head-row">
      {back && <motion.button whileTap={{ scale: 0.85 }} className="icon-btn" onClick={back} aria-label="Back"><Ic.chevL size={19} /></motion.button>}
      <div><div className="sec-tag">{tag}</div><h1 className="f-disp page-title">{title}</h1></div>
      {action}
    </div>
    {sub && <p className="page-sub">{sub}</p>}
  </header>
);

/* ============================================================
   DASHBOARD
   ============================================================ */
const Dashboard = () => {
  const { data, nav, T, settings } = useApp();
  const q = settings.quirky;
  const now = Date.now(), wk = 7 * 864e5;
  const thisWk = data.brews.filter(b => now - new Date(b.startTime).getTime() < wk).length;
  const lastWk = data.brews.filter(b => { const d = now - new Date(b.startTime).getTime(); return d >= wk && d < 2 * wk; }).length;
  const trend = thisWk - lastWk;
  const inStock = data.beans.filter(b => b.currentWeight > 0).length;
  const rated = data.brews.filter(b => b.rating);
  const avg = rated.length ? rated.reduce((a, b) => a + b.rating, 0) / rated.length : 0;
  const lowBeans = data.beans.filter(b => b.currentWeight < b.lowStockThreshold);
  const recent = [...data.brews].sort((a, b) => new Date(b.startTime) - new Date(a.startTime)).slice(0, 3);
  const latestBean = [...data.beans].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const champion = useMemo(() => {
    if (data.brews.length < 3) return null;
    const byBean = {};
    data.brews.forEach(b => { if (b.beanId && b.rating) (byBean[b.beanId] = byBean[b.beanId] || []).push(b.rating); });
    let best = null;
    Object.entries(byBean).forEach(([id, rs]) => { if (rs.length >= 2) { const a = rs.reduce((x, y) => x + y, 0) / rs.length; if (!best || a > best.avg) best = { id: Number(id), avg: best.avg }; } });
    const bean = best && data.beans.find(b => b.id === best.id);
    return bean ? { bean, avg: best.avg } : null;
      }, [data]);
      const sterile = data.beans.length === 0 && data.brews.length === 0;
      const beanName = id => { const b = data.beans.find(x => x.id === id); return b ? b.name : 'No specimen'; };

      return (
        <div className="page">
          <header className="dash-head">
            <div className="logo-row">
              <motion.div className="logo-beaker" whileHover={{ rotate: -8, scale: 1.06 }} transition={{ type: 'spring' }}><Ic.flask size={34} sw={2.4} /></motion.div>
              <div>
                <h1 className="f-disp app-title">COFFEE<span className="title-accent">LAB</span></h1>
                <div className="app-sub f-mono">BREW SCIENCE UNIT · v1.0 · LOCAL-FIRST</div>
              </div>
              <motion.button whileTap={{ scale: 0.88, rotate: 40 }} className="icon-btn gear-btn" onClick={() => nav('settings', {})} aria-label={T('settings')}><Ic.gear size={20} /></motion.button>
            </div>
            <motion.p className="greeting" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>{greeting(q)}</motion.p>
          </header>

      {sterile ? (
        <motion.div className="card sterile-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <EmptyBeaker />
          <h2 className="f-disp sterile-title">{T('emptyBeans')}</h2>
          <p className="sterile-sub">{q ? 'Let’s fix that.' : 'Add a bean or start brewing to begin.'}</p>
          <div className="sterile-actions">
            <motion.button whileTap={{ scale: 0.94 }} className="btn btn-primary" onClick={() => nav('beans', { add: true })}><Ic.plus size={15} /> {T('addBean')}</motion.button>
            <motion.button whileTap={{ scale: 0.94 }} className="btn btn-plasma" onClick={() => nav('brew', {})}><Ic.timer size={15} /> Brew with curated recipe</motion.button>
          </div>
        </motion.div>
      ) : (
        <>
          <div className="sec-tag">— VITAL SIGNS</div>
          <div className="stats-row">
            {[
              { label: 'Brews / 7d', val: thisWk, icon: Ic.timer, extra: <span className={'trend ' + (trend > 0 ? 'up' : trend < 0 ? 'down' : 'flat')}>{trend > 0 ? <Ic.up size={11} /> : trend < 0 ? <Ic.down size={11} /> : '—'} {trend !== 0 ? Math.abs(trend) : 'steady'}</span> },
              { label: 'Beans in stock', val: inStock, icon: Ic.bean, extra: <span className="stat-sub f-mono">{data.beans.length} total</span> },
              { label: 'Avg rating', val: rated.length ? avg.toFixed(1) : '—', icon: Ic.trophy, extra: <Stars value={avg} size={12} /> },
            ].map((s, i) => (
              <motion.div key={s.label} className="card stat-card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}>
                <div className="stat-top"><s.icon size={16} /><span className="stat-label">{s.label}</span></div>
                <div className="stat-val f-mono">{s.val}</div>
                <div className="stat-extra">{s.extra}</div>
              </motion.div>
            ))}
          </div>

          {lowBeans.length > 0 && (
            <div className="low-banner" key="low">
              <div className="hazard-strip" />
              <div className="low-body">
                <Ic.alert size={18} className="low-ic" />
                <div>
                  <strong>{q ? '⚠️ ' + lowBeans[0].name + ' is critically low at ' + lowBeans[0].currentWeight + 'g. Panic accordingly.' : lowBeans[0].name + ' is running low (' + lowBeans[0].currentWeight + 'g left).'}</strong>
                  {lowBeans.length > 1 && <div className="low-more f-mono">+{lowBeans.length - 1} more specimen{lowBeans.length > 2 ? 's' : ''} affected</div>}
                </div>
              </div>
            </div>
          )}

          <motion.div className="card brew-now" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
            whileHover={{ y: -3 }} onClick={() => latestBean ? nav('brew', { beanId: latestBean.id }) : nav('beans', { add: true })}>
            <div className="brew-now-left">
              <div className="sec-tag">{q ? '— READY TO EXPERIMENT' : '— Quick start'}</div>
              <div className="f-disp brew-now-title">{latestBean ? <>BREW WITH<br /><span className="neon-text">{latestBean.name.toUpperCase()}</span></> : 'ACQUIRE SPECIMEN →'}</div>
              {latestBean && <div className="f-mono brew-now-meta">{latestBean.currentWeight}g remaining · {latestBean.roaster}</div>}
            </div>
            <div className="brew-now-btn"><Ic.timer size={26} /></div>
          </motion.div>

          {champion && (
            <motion.div className="champion" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.18 }}>
              <Ic.trophy size={20} /><span><b>CURRENT CHAMPION:</b> {champion.bean.name} — <span className="f-mono">{champion.avg.toFixed(1)}★</span> avg</span>
              <FreshnessStat bean={champion.bean} compact />
            </motion.div>
          )}

          <div className="sec-head">
            <div className="sec-tag">— RECENT EXPERIMENTS</div>
            {data.brews.length > 3 && <button className="link-btn f-mono" onClick={() => nav('reports', { tab: 'journal' })}>VIEW ALL →</button>}
          </div>
          {recent.length === 0 ? (
            <div className="card flat-card empty-mini">{T('emptyBrews')}</div>
          ) : (
            <div className="recent-row">
              {recent.map((b, i) => { const MI = METHOD_ICON[b.method] || Ic.flask; return (
                <motion.button key={b.id} className="card recent-card" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}
                  whileTap={{ scale: 0.96 }} onClick={() => nav('reports', { tab: 'journal', focus: b.id })}>
                  <div className="recent-ic" style={{ color: METHODS[b.method]?.color }}><MI size={18} /></div>
                  <div className="recent-name">{b.recipeName}</div>
                  <div className="recent-bean f-mono">{beanName(b.beanId)}</div>
                  <Stars value={b.rating || 0} size={11} />
                  <div className="recent-ago f-mono">{timeAgo(b.startTime)}</div>
                </motion.button>); })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ============================================================
   BREW FLOW
   ============================================================ */
function useBrewTimer(steps, onComplete) {
  const [idx, setIdx] = useState(0);
  const [running, setRunning] = useState(true);
  const [rem, setRem] = useState(steps[0]?.duration || 0);
  const remRef = useRef(steps[0]?.duration || 0);
  const lastTick = useRef(Date.now());
  const elapsed = useRef(steps.map(() => 0));
  const completed = useRef([]);
  const done = useRef(false);
  const idxRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const advance = useCallback(() => {
    if (done.current) return;
    const i = idxRef.current, cur = steps[i];
    completed.current.push({ stepId: cur.id, plannedDuration: cur.duration, actualDuration: Math.round((elapsed.current[i] || 0) / 1000) });
    if (i + 1 >= steps.length) { done.current = true; onCompleteRef.current(completed.current); return; }
    idxRef.current = i + 1; remRef.current = steps[i + 1].duration; setIdx(i + 1); setRem(steps[i + 1].duration);
    if (document.visibilityState === 'visible') { playStepBeep(); navigator.vibrate && navigator.vibrate(60); }
  }, [steps]);
  useEffect(() => {
    if (!running) return;
    lastTick.current = Date.now();
    const iv = setInterval(() => {
      const now = Date.now(), dt = now - lastTick.current; lastTick.current = now;
      elapsed.current[idxRef.current] += dt;
      const r = remRef.current - dt / 1000;
      if (r <= 0) advance(); else { remRef.current = r; setRem(r); }
    }, 100);
    return () => clearInterval(iv);
  }, [running, advance]);
  useEffect(() => {
    let wl = null;
    const req = async () => { try { wl = await navigator.wakeLock?.request('screen'); } catch (e) { /* */ } };
    req();
    const onVis = () => { if (document.visibilityState === 'visible') req(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { document.removeEventListener('visibilitychange', onVis); try { wl?.release(); } catch (e) { /* */ } };
  }, []);
  const totalElapsed = steps.slice(0, idx).reduce((a, s) => a + s.duration, 0) + (steps[idx] ? steps[idx].duration - rem : 0);
  return { idx, rem, running, setRunning, skip: advance, totalElapsed };
}

const TimerScreen = ({ recipe, water, onComplete, onAbort }) => {
  const { settings, T } = useApp();
  const q = settings.quirky;
  const steps = recipe.steps;
  const [abortOpen, setAbortOpen] = useState(false);
  const t = useBrewTimer(steps, onComplete);
  const cur = steps[t.idx];
  /* Step schema v2: read `kind` with a fallback to legacy `action`. The
     migration adds `kind` on first load after deploy v7, but defensive read
     here means a half-migrated recipe doesn't render with the wrong label. */
  const kind = cur.kind || cur.action || 'pour';
  const meta = ACTION_META[kind] || ACTION_META.pour;
  const AI = ACTION_ICON[kind] || Ic.droplet;
  const isVol = kind === 'pour' || kind === 'plunge';
  const state = t.rem <= 5 ? 'danger' : t.rem <= 15 ? 'warn' : 'ok';
  /* Compute the live target weight for the current step. Recipes are
     scale-invariant: each step stores `targetWeightPct` (targetWeight /
     waterAmount at recipe-creation time). We multiply by the actual brew
     `water` (which the user can change in the setup screen) to get the
     grams to pour for this step. Falls back to the step's absolute
     targetWeight when pct is missing (legacy / pre-migration data).
     Only pour/plunge steps have a target weight. */
  const liveTarget = isVol && cur.targetWeightPct != null
    ? Math.round((Number(water) || recipe.waterAmount) * cur.targetWeightPct)
    : isVol ? cur.targetWeight : null;
  /* Compute the pour-volume for the current step — how many grams of water
     this step is asking the user to pour. It's the *delta* between this
     step's target and the previous pour/plunge step's target (or 0 if
     no previous volume step). Used to compute the expected pour duration
     from flowRate. */
  const prevTarget = (() => {
    for (let i = t.idx - 1; i >= 0; i--) {
      const p = steps[i];
      if (p.kind === 'pour' || p.kind === 'plunge') {
        if (p.targetWeightPct != null) return Math.round((Number(water) || recipe.waterAmount) * p.targetWeightPct);
        return p.targetWeight || 0;
      }
    }
    return 0;
  })();
  const pourVolume = (isVol && liveTarget != null && liveTarget > prevTarget) ? liveTarget - prevTarget : null;
  /* Display label for the flow rate. Shown on pour/plunge steps only.
     Format: "100g @ 6.0 g/s ≈ 17s pour, 13s settle" if the step's duration
     is longer than the pour time (the rest is a settle/wait). */
  const flowLabel = (isVol && cur.flowRate != null && pourVolume != null && pourVolume > 0) ? (() => {
    const pourSec = pourVolume / cur.flowRate;
    const stepSec = Number(cur.duration) || pourSec;
    if (stepSec > pourSec + 1) {
      return `${pourVolume}g @ ${cur.flowRate.toFixed(1)} g/s · ${Math.round(pourSec)}s pour + ${Math.round(stepSec - pourSec)}s settle`;
    }
    return `${pourVolume}g @ ${cur.flowRate.toFixed(1)} g/s ≈ ${Math.round(pourSec)}s`;
  })() : null;
  const frac = cur.duration ? t.rem / cur.duration : 0;
  return (
    <motion.div className="timer-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="timer-top">
        <div className="f-mono timer-recipe"><Ic.timer size={13} /> {recipe.name.toUpperCase()} · T+{fmtTime(t.totalElapsed)}</div>
        <button className="btn btn-ghost btn-sm" onClick={() => setAbortOpen(true)}>{T('abort')}</button>
      </div>
      <div className="timer-mid">
        <div key={cur.id} className="step-block fx-step">
          <div className="step-action-row">
            <span className="step-action-ic" style={{ color: meta.color }}><AI size={26} /></span>
            <h2 className="f-disp step-action">{q ? meta.quirky : meta.label.toUpperCase()}</h2>
          </div>
          <ProgressRing progress={frac} state={state}>
            <div className={'f-mono ring-time' + (state === 'danger' ? ' ring-danger' : '')}>{fmtTime(t.rem)}</div>
            <div className="ring-step f-mono">STEP {t.idx + 1}/{steps.length}</div>
          </ProgressRing>
          <p className="step-desc">{cur.description}</p>
          {liveTarget != null && <div className="target-chip f-mono"><Ic.weight size={13} /> TARGET {liveTarget}g</div>}
          {flowLabel && <div className="flow-chip f-mono"><Ic.droplet size={13} /> {flowLabel}</div>}
          {cur.tip && <p className="step-tip">“{cur.tip}”</p>}
        </div>
      </div>
      <div className="timer-controls">
        <motion.button whileTap={{ scale: 0.9 }} className="ctl-btn" onClick={() => { t.setRunning(!t.running); playClick(); }} aria-label={t.running ? 'Pause' : 'Play'}>
          {t.running ? <Ic.pause size={30} /> : <Ic.play size={30} />}
        </motion.button>
        <motion.button whileTap={{ scale: 0.9 }} className="ctl-btn ctl-skip" onClick={t.skip} aria-label={T('skip')}><Ic.skip size={22} /></motion.button>
      </div>
      <div className="step-dots">
        {steps.map((s, i) => {
          const sKind = s.kind || s.action || 'pour';
          return (
            <span key={s.id} className={'sdot' + (i < t.idx ? ' sdot-done' : i === t.idx ? ' sdot-cur' : '')}
              style={i === t.idx ? { background: (ACTION_META[sKind] || {}).color } : undefined} />
          );
        })}
      </div>
      <Confirm open={abortOpen} danger title={q ? 'ABORT MISSION?' : 'Cancel brew?'}
        body={q ? 'All experimental data from this run will be lost. The beans died for nothing.' : 'Progress in this brew will be discarded.'}
        confirmLabel={T('abort')} onYes={onAbort} onNo={() => setAbortOpen(false)} />
    </motion.div>
  );
};

const BrewFlow = () => {
  const { data, route, nav, T, settings, ops, toast } = useApp();
  const q = settings.quirky;
  const [phase, setPhase] = useState('method');
  const [method, setMethod] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [setup, setSetup] = useState(null);
  const [result, setResult] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [confetti, setConfetti] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  /* Track which brew-setup field the user last edited ('dose' | 'water' |
     'ratio' | 'bean'). Used in the analytics hint, not the math. Must live
     at the top of the component — hooks cannot be conditional. The actual
     field-coupling lives in the onDose/onWater/onRatio handlers below: they
     enforce `water = dose × ratio` exactly. Whichever input the user types
     into becomes the independent variable; the other two adjust to keep the
     math consistent. */
  const lastEdited = useRef('water');

  const defaultSetup = (r, beanId) => ({ beanId: beanId ? Number(beanId) : null, dose: r.coffeeDose, water: r.waterAmount, grind: r.grindSize, temp: r.temperature });
  useEffect(() => {
    const p = route.params || {};
    if (p.recipeId) { const r = data.recipes.find(x => x.id === p.recipeId || x.id === Number(p.recipeId)); if (r) { setMethod(r.method); setRecipe(r); setSetup(defaultSetup(r, p.beanId)); setPhase('setup'); } }
  }, []);
  const pickRecipe = r => { setRecipe(r); setSetup(defaultSetup(r, route.params?.beanId)); setPhase('setup'); };
  const startBrew = () => { playStepBeep(); navigator.vibrate && navigator.vibrate([40, 60, 40]); setStartTime(new Date().toISOString()); setPhase('timer'); };
  const finishBrew = stepsCompleted => { setResult(stepsCompleted); setConfetti(true); if (document.visibilityState === 'visible') playEureka(); navigator.vibrate && navigator.vibrate([80, 60, 80, 60, 160]); setPhase('rate'); setTimeout(() => setConfetti(false), 2900); };
  const saveBrew = () => {
    const brew = { id: uid(), beanId: setup.beanId || null, recipeId: recipe.id, recipeName: recipe.name, method: recipe.method,
      coffeeDose: Number(setup.dose) || recipe.coffeeDose, waterAmount: Number(setup.water) || recipe.waterAmount, grindSize: setup.grind,
      temperature: Number(setup.temp) || recipe.temperature, startTime, endTime: new Date().toISOString(), stepsCompleted: result,
      rating: rating || null, notes: notes.trim() || null, createdAt: new Date().toISOString() };
    const res = ops.addBrew(brew);
    toast(q ? 'EXPERIMENT COMMITTED TO ARCHIVE' : 'Brew saved to journal');
    if (res.beanEmptied) setTimeout(() => toast(`You just brewed your last grams of ${res.beanName}!`, 'warn'), 500);
    nav('dashboard', {});
  };
  const setAbortSafe = () => { setPhase('method'); setRecipe(null); setMethod(null); setSetup(null); };

  if (phase === 'method') return (
    <div className="page">
      <PageHead tag="— BREW SEQUENCE" title="SELECT METHOD" sub={q ? 'Choose your weapon of extraction.' : 'Pick a brewing method.'}
        action={
          <motion.button whileTap={{ scale: 0.94 }} className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormOpen(true); }} aria-label="Add new recipe">
            <Ic.plus size={14} /> {q ? 'NEW PROTOCOL' : 'NEW RECIPE'}
          </motion.button>
        } />
      <div className="method-grid">
        {Object.entries(METHODS).map(([key, m], i) => { const MI = METHOD_ICON[key]; const count = data.recipes.filter(r => r.method === key).length; return (
          <motion.button key={key} className="card method-card" initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
            whileHover={{ y: -4 }} whileTap={{ scale: 0.96 }} onClick={() => { setMethod(key); setPhase('recipe'); playClick(); }}>
            <div className="method-ic" style={{ background: m.color }}><MI size={26} sw={2.4} /></div>
            <div className="f-disp method-name">{m.label}</div>
            <div className="method-sub">{m.sub}</div>
            <div className="f-mono method-count">{count} protocol{count !== 1 ? 's' : ''} available</div>
          </motion.button>); })}
      </div>
    </div>
  );

  if (phase === 'recipe') {
    const list = data.recipes.filter(r => r.method === method).sort((a, b) => (b.isCurated - a.isCurated) || (new Date(b.createdAt) - new Date(a.createdAt)));
    const m = METHODS[method];
    const lastRating = rid => { const bs = data.brews.filter(b => b.recipeId === rid && b.rating).sort((a, b) => new Date(b.startTime) - new Date(a.startTime)); return bs[0]?.rating; };
    return (
      <div className="page">
        <PageHead back={() => setPhase('method')} tag="— PROTOCOL LIBRARY" title={`${m.label.toUpperCase()} PROTOCOLS`}
          sub={q ? `${list.length} sanctioned procedure${list.length !== 1 ? 's' : ''} on file.` : `${list.length} recipe${list.length !== 1 ? 's' : ''} for this method.`}
          action={
            <motion.button whileTap={{ scale: 0.94 }} className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormOpen(true); }} aria-label="Add new recipe">
              <Ic.plus size={14} /> {q ? 'NEW PROTOCOL' : 'NEW RECIPE'}
            </motion.button>
          } />
        {list.length === 0 && <div className="card flat-card empty-mini">{T('emptyRecipes')}</div>}
        <div className="list-col">
          {list.map((r, i) => (
            <motion.button key={r.id} className="card proto-card" initial={{ opacity: 0, x: 26 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}
              whileTap={{ scale: 0.97 }} onClick={() => pickRecipe(r)}>
              <div className="proto-top"><span className="f-disp proto-name">{r.name}</span><Stamp kind={r.isCurated ? 'official' : 'exp'}>{r.isCurated ? 'OFFICIAL' : 'EXPERIMENT'}</Stamp></div>
              <div className="f-mono proto-stats">{r.coffeeDose}g · {r.waterAmount}ml · {r.temperature}°C · {fmtTime(recipeTotal(r))}</div>
              <div className="proto-meta"><span className="f-mono proto-grind">{r.grindSize}</span>{lastRating(r.id) && <span className="proto-last">LAST <Stars value={lastRating(r.id)} size={11} /></span>}</div>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  if (phase === 'setup') {
    const beans = [...data.beans].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const selBean = beans.find(b => b.id === setup.beanId);
    /* Brew setup: dose / water / ratio are coupled via `water = dose × ratio`.
       We let the user touch any one and the other two auto-adjust. The
       `lastEdited` ref (declared at the top of BrewFlow — hooks cannot be
       conditional) disambiguates which field the user actually typed in;
       informational only. Bean selection also nudges the dose down to the
       bean's remaining mass if the recipe's default would exceed it. Ratio
       is rendered as `1:X`; on water edit we leave dose alone and let ratio
       re-derive on render. */
    const doseN = Number(setup.dose) || 0;
    const waterN = Number(setup.water) || 0;
    const ratioN = doseN > 0 ? waterN / doseN : 0;
    /* Field coupling — `water = dose × ratio` is the canonical relation, so
       whichever input the user touches becomes the driver and the other two
       adjust to keep the readouts consistent.

       Before: only onDose and onRatio updated the coupled fields; onWater
       just set the new water, leaving ratio stale against the dose. The user
       reported this as "the numbers stay decoupled". Fix: every handler
       recomputes the two fields it's not driving. */
    const onDose = v => {
      lastEdited.current = 'dose';
      const newDose = Number(v) || 0;
      setSetup(s => {
        const r = (Number(s.water) || 0) / (Number(s.dose) || 1); // keep current ratio
        return { ...s, dose: newDose, water: Math.max(1, Math.round(newDose * r)) };
      });
    };
    const onWater = v => {
      lastEdited.current = 'water';
      const newWater = Number(v) || 0;
      setSetup(s => {
        const dose = Number(s.dose) || 0;
        if (dose <= 0) return { ...s, water: newWater }; // ratio undefined until dose enters
        // Keep dose fixed; recompute ratio implicitly. Nothing else to update —
        // ratio reads as `water/dose` at render time.
        return { ...s, water: newWater };
      });
    };
    const onRatio = v => {
      lastEdited.current = 'ratio';
      const newRatio = Number(v) || 0;
      setSetup(s => {
        const dose = Number(s.dose) || 0;
        // If dose is missing, deriving water from ratio needs a fallback — use
        // the recipe default rather than coercing a 0 that'd brick downstream.
        if (dose <= 0) return { ...s, water: Math.max(1, Math.round(recipe.coffeeDose * newRatio)) };
        return { ...s, water: Math.max(1, Math.round(dose * newRatio)) };
      });
    };
    const onBean = id => { lastEdited.current = 'bean'; setSetup(s => { const bean = beans.find(b => b.id === Number(id)); const newDose = bean ? Math.min(recipe.coffeeDose, Math.max(0, bean.currentWeight)) : recipe.coffeeDose; const r = (Number(s.water) || 0) / (Number(s.dose) || 1); return { ...s, beanId: id ? Number(id) : null, dose: newDose, water: Math.max(1, Math.round(newDose * r)) }; }); };
    const ratioDisplay = (doseN > 0 && waterN > 0) ? `1:${ratioN.toFixed(1)}` : '—';
    return (
      <div className="page">
        <PageHead back={() => setPhase('recipe')} tag="— EXPERIMENT PREP" title="PREP YOUR SPECIMEN"
          sub={q ? `Protocol: ${recipe.name}. Calibrate your variables.` : `Recipe: ${recipe.name}. Adjust values as needed.`} />
        <motion.div className="card setup-card" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}>
          <Field label="Specimen (bean)">
            <div className="select-wrap">
              <select className="inp" value={setup.beanId ?? ''} onChange={e => onBean(e.target.value)}>
                <option value="">— No specimen —</option>
                {beans.map(b => <option key={b.id} value={b.id}>{b.name} ({b.currentWeight}g)</option>)}
              </select><Ic.chevD size={15} className="select-chev" />
            </div>
          </Field>
          {selBean && selBean.currentWeight < Number(setup.dose) && <div className="warn-line f-mono"><Ic.alert size={13} /> {q ? 'SPECIMEN MASS INSUFFICIENT FOR THIS DOSE' : 'Not enough of this bean for the dose.'}</div>}
          <div className="grid-2">
            <Field label="Dose (g)"><input className="inp f-mono" type="number" step="0.5" min="1" value={setup.dose} onChange={e => onDose(e.target.value)} /></Field>
            <Field label="Water (ml)"><input className="inp f-mono" type="number" step="5" min="10" value={setup.water} onChange={e => onWater(e.target.value)} /></Field>
            <Field label="Grind size"><input className="inp" type="text" value={setup.grind} onChange={e => setSetup({ ...setup, grind: e.target.value })} /></Field>
            <Field label="Temp (°C)"><input className="inp f-mono" type="number" min="60" max="100" value={setup.temp} onChange={e => setSetup({ ...setup, temp: e.target.value })} /></Field>
          </div>
          <div className="ratio-row">
            <span className="f-mono ratio-label">RATIO</span>
            <div className="ratio-controls">
              <span className="ratio-readout f-mono">{ratioDisplay}</span>
              <Field label="Adjust ratio" hint="water auto-scales"><input className="inp f-mono inp-sm ratio-input" type="number" step="0.1" min="8" max="25" value={ratioN > 0 ? ratioN.toFixed(1) : ''} onChange={e => onRatio(e.target.value)} /></Field>
            </div>
          </div>
          <motion.button whileTap={{ scale: 0.95 }} className="btn btn-primary btn-start" onClick={startBrew}><Ic.play size={16} /> {T('start')}</motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      {phase === 'timer' && <TimerScreen key="timer" recipe={recipe} water={Number(setup.water) || recipe.waterAmount} onComplete={finishBrew} onAbort={setAbortSafe} />}
      {phase === 'rate' && (
        <Overlay open={true} panelClass="card rate-card" panelAnim="scale" backdropClass="rate-back">
          {confetti && <ConfettiBurst />}
          <div className="eureka-badge f-mono">EXPERIMENT № {data.brews.length + 1}</div>
          <h2 className="f-disp eureka-title">{q ? 'EUREKA!' : 'Brew complete'}</h2>
          <p className="eureka-sub">{q ? 'Extraction successful. The lab applauds you.' : `${recipe.name} finished in ${fmtTime((result || []).reduce((a, s) => a + s.actualDuration, 0))}.`}</p>
          <div className="sec-tag" style={{ textAlign: 'center' }}>— {T('rate')}</div>
          <RatingInput value={rating} onChange={setRating} />
          <textarea className="inp rate-notes" rows={3} placeholder={q ? 'Observations? Sour? Bitter? Transcendent?' : 'Post-brew notes…'} value={notes} onChange={e => setNotes(e.target.value)} />
          <motion.button whileTap={{ scale: 0.95 }} className="btn btn-primary btn-block" onClick={saveBrew}><Ic.check size={15} /> {T('save')}</motion.button>
          <button className="link-btn f-mono discard-link" onClick={() => setDiscardOpen(true)}>discard results</button>
          <Confirm open={discardOpen} danger title="DISCARD RESULTS?" body="This experiment will not be recorded." confirmLabel="DISCARD" onYes={() => nav('dashboard', {})} onNo={() => setDiscardOpen(false)} />
        </Overlay>
      )}
    </>
  );
};

/* ============================================================
   BEANS
   ============================================================ */
const BeanPhoto = ({ bean, size = '100%', quirky = true }) => bean.photo
  ? <img src={bean.photo} alt={bean.name} className="bean-photo" style={{ height: size }} />
  : <div className="bean-photo bean-photo-empty" style={{ height: size }}><span className="bean-photo-empty-label f-mono">{quirky ? 'NO SPECIMEN PHOTOGRAPHED' : 'NO PHOTO YET'}</span></div>;

const Beans = () => {
  const { data, nav, T, settings, ops, toast, route } = useApp();
  const q = settings.quirky;
  const [filter, setFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  useEffect(() => { if (route.params?.add) { setEditing(null); setFormOpen(true); } }, []);
  const roastMatch = (b, f) => f === 'Light' ? ['Light', 'Medium-Light'].includes(b.roastLevel) : f === 'Medium' ? b.roastLevel === 'Medium' : f === 'Dark' ? ['Medium-Dark', 'Dark'].includes(b.roastLevel) : true;
  const list = data.beans.filter(b => filter === 'all' ? true : filter === 'low' ? b.currentWeight < b.lowStockThreshold : roastMatch(b, filter));
  const brewCountFor = id => data.brews.filter(b => b.beanId === id).length;
  return (
    <div className="page">
      <PageHead tag="— INVENTORY" title="SPECIMENS" sub={q ? `${data.beans.length} biological assets under observation.` : `${data.beans.length} beans tracked.`} />
      <div className="head-action-row">
        <div className="chip-row">
          {['all', 'Light', 'Medium', 'Dark', 'low'].map(f => (
            <Chip key={f} active={filter === f} onClick={() => { setFilter(f); playClick(); }}>{f === 'all' ? 'All' : f === 'low' ? '⚠ Low stock' : f}</Chip>
          ))}
        </div>
        <motion.button whileTap={{ scale: 0.93 }} className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setFormOpen(true); }}><Ic.plus size={14} /> {T('addBean')}</motion.button>
      </div>
      {list.length === 0 ? (
        <div className="card flat-card empty-mini">{data.beans.length === 0 ? T('emptyBeans') : 'No specimens match this filter.'}</div>
      ) : (
        <div className="bean-grid">
          {list.map((b, i) => (
            <motion.button key={b.id} className="card bean-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.04 * i }}
              whileTap={{ scale: 0.96 }} onClick={() => setDetail(b)}>
              <div className="bean-photo-wrap"><BeanPhoto bean={b} size="104px" /></div>
              <div className="bean-card-body">
                <div className="bean-name">{b.name}</div>
                <div className="bean-roaster f-mono">{b.roaster}</div>
                <div className="bean-stats">
                  <RoastStat bean={b} />
                  <WeightStat bean={b} />
                  <FreshnessStat bean={b} />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      )}
      <Sheet open={!!detail} onClose={() => setDetail(null)} title={detail?.name || ''} tag="SPECIMEN FILE">
        {detail && (() => { const b = data.beans.find(x => x.id === detail.id) || detail; return (
          <div className="bean-detail">
            <BeanPhoto bean={b} size="170px" />
            <div className="bean-detail-rows">
              <div><span className="dt">Nickname</span><span className="dd">{b.nickname || '—'}</span></div>
              <div><span className="dt">Roaster</span><span className="dd">{b.roaster}</span></div>
              <div><span className="dt">Origin</span><span className="dd">{[b.originCountry, b.originRegion].filter(Boolean).join(' · ') || '—'}</span></div>
              <div><span className="dt">Processing</span><span className="dd">{b.processingMethod || '—'}</span></div>
              <div><span className="dt">Roast</span><span className="dd"><RoastStat bean={b} /></span></div>
              <div><span className="dt">Roast date</span><span className="dd f-mono">{b.roastDate ? new Date(b.roastDate).toLocaleDateString() : '—'}</span></div>
              <div><span className="dt">Purchased</span><span className="dd f-mono">{b.purchaseDate ? new Date(b.purchaseDate).toLocaleDateString() : '—'}{b.price ? ` · ${b.price}` : ''}</span></div>
              <div><span className="dt">Mass</span><span className="dd"><WeightStat bean={b} /> <span className="f-mono dd-sub">/ {b.startingWeight}g</span></span></div>
              <div><span className="dt">Low threshold</span><span className="dd f-mono">{b.lowStockThreshold}g</span></div>
              <div><span className="dt">Brews</span><span className="dd f-mono">{brewCountFor(b.id)}</span></div>
            </div>
            {b.flavorTags?.length > 0 && <div className="tag-row">{b.flavorTags.map(t => <span key={t} className="flavor-chip">{t}</span>)}</div>}
            <div className="bean-detail-fresh"><FreshnessStat bean={b} /></div>
            {b.tastingNotes && <p className="tasting-notes">“{b.tastingNotes}”</p>}
            <div className="sheet-actions">
              <motion.button whileTap={{ scale: 0.94 }} className="btn btn-primary" onClick={() => nav('brew', { beanId: b.id })}><Ic.timer size={15} /> Brew with this</motion.button>
              <button className="btn" onClick={() => { setEditing(b); setDetail(null); setFormOpen(true); }}><Ic.edit size={14} /> Edit</button>
              <button className="btn btn-danger" onClick={() => setDelTarget(b)}><Ic.trash size={14} /> {T('del')}</button>
            </div>
          </div>); })()}
      </Sheet>
      <BeanForm open={formOpen} bean={editing} onClose={() => setFormOpen(false)} />
      <Confirm open={!!delTarget} danger title={q ? 'TERMINATE SPECIMEN?' : 'Delete this bean?'}
        body={delTarget ? `This will erase ${brewCountFor(delTarget.id)} brew record${brewCountFor(delTarget.id) !== 1 ? 's' : ''} tied to ${delTarget.name}. Are you sure?` : ''}
        confirmLabel={T('del')}
        onYes={() => { ops.deleteBean(delTarget.id); toast(q ? 'SPECIMEN TERMINATED' : 'Bean deleted', 'warn'); setDelTarget(null); setDetail(null); }}
        onNo={() => setDelTarget(null)} />
    </div>
  );
};

const BeanForm = ({ open, bean, onClose }) => {
  const { ops, toast, T, settings } = useApp();
  const q = settings.quirky;
  const blank = { name: '', nickname: '', roaster: '', originCountry: '', originRegion: '', processingMethod: '', roastLevel: 'Medium', roastDate: '', purchaseDate: '', price: '', startingWeight: 250, currentWeight: 250, lowStockThreshold: 50, tastingNotes: '', flavorTags: [], photo: null };
  const [f, setF] = useState(blank);
  const camRef = useRef(null), fileRef = useRef(null);
  useEffect(() => { if (open) setF(bean ? { ...blank, ...bean, roastDate: bean.roastDate ? String(bean.roastDate).slice(0, 10) : '', purchaseDate: bean.purchaseDate ? String(bean.purchaseDate).slice(0, 10) : '' } : blank); }, [open, bean]);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const onPhotoFile = e => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) { toast('Camera access denied. Use file picker instead.', 'warn'); return; }
    const url = URL.createObjectURL(file); const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 640 / Math.max(img.width, img.height));
      const c = document.createElement('canvas'); c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url);
      set('photo', c.toDataURL('image/jpeg', 0.82));
    };
    img.src = url;
  };
  const save = () => {
    if (!f.name.trim() || !f.roaster.trim()) { toast('Name and roaster are required.', 'warn'); return; }
    const startW = Number(f.startingWeight) || 0; if (startW <= 0) { toast('Starting weight must be > 0.', 'warn'); return; }
    const rec = { ...f, name: f.name.trim(), roaster: f.roaster.trim(), startingWeight: startW,
      currentWeight: bean ? (Number(f.currentWeight) || 0) : startW, lowStockThreshold: Number(f.lowStockThreshold) || 50, price: f.price ? String(f.price) : null };
    ops.saveBean(rec, bean?.id); toast(q ? 'SPECIMEN ACQUIRED' : 'Bean saved'); onClose();
  };
  const roastIdx = ROAST_LEVELS.findIndex(r => r.name === f.roastLevel);
  const stagger = { hidden: { opacity: 0, y: 14 }, show: i => ({ opacity: 1, y: 0, transition: { delay: 0.03 * i } }) };
  let fi = 0;
  return (
    <Sheet open={open} onClose={onClose} title={bean ? 'EDIT SPECIMEN' : T('addBean').toUpperCase()} tag={bean ? `SPEC-${String(bean.id).padStart(3, '0')}` : 'NEW INTAKE'}>
      <div className="form-col">
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}><Field label="Name *"><input className="inp" value={f.name} onChange={e => set('name', e.target.value)} placeholder="Ethiopia Yirgacheffe" /></Field></motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}><Field label="Nickname"><input className="inp" value={f.nickname} onChange={e => set('nickname', e.target.value)} placeholder="The Mysterious Elixir" /></Field></motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}><Field label="Roaster *"><input className="inp" value={f.roaster} onChange={e => set('roaster', e.target.value)} placeholder="Onyx Coffee Lab" /></Field></motion.div>
        <motion.div className="grid-2" variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Origin country"><input className="inp" value={f.originCountry} onChange={e => set('originCountry', e.target.value)} /></Field>
          <Field label="Origin region"><input className="inp" value={f.originRegion} onChange={e => set('originRegion', e.target.value)} /></Field>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Processing method"><div className="select-wrap"><select className="inp" value={f.processingMethod} onChange={e => set('processingMethod', e.target.value)}><option value="">—</option>{PROCESSING_OPTS.map(p => <option key={p} value={p}>{p}</option>)}</select><Ic.chevD size={15} className="select-chev" /></div></Field>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Roast level *">
            <div className="roast-picker">
              {ROAST_LEVELS.map((r, i) => (
                <motion.button key={r.name} type="button" whileTap={{ scale: 1.25 }} className={'roast-bean' + (i <= roastIdx ? ' roast-on' : '')} onClick={() => set('roastLevel', r.name)} title={r.name}>
                  <Ic.bean size={30} fill={i <= roastIdx ? r.color : 'none'} style={{ color: i <= roastIdx ? r.color : 'var(--muted)' }} />
                </motion.button>
              ))}
            </div>
            <div className="roast-label f-mono">{f.roastLevel.toUpperCase()}</div>
          </Field>
        </motion.div>
        <motion.div className="date-grid" variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Roast date"><input className="inp f-mono" type="date" value={f.roastDate} onChange={e => set('roastDate', e.target.value)} /></Field>
          <Field label="Purchase date"><input className="inp f-mono" type="date" value={f.purchaseDate} onChange={e => set('purchaseDate', e.target.value)} /></Field>
        </motion.div>
        <motion.div className="grid-3" variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Price"><input className="inp f-mono" type="text" value={f.price} onChange={e => set('price', e.target.value)} placeholder="$22" /></Field>
          <Field label="Start wt (g) *"><input className="inp f-mono" type="number" min="1" value={f.startingWeight} onChange={e => set('startingWeight', e.target.value)} /></Field>
          <Field label="Now (g)"><input className="inp f-mono" type="number" min="0" value={bean ? f.currentWeight : f.startingWeight} onChange={e => set('currentWeight', e.target.value)} disabled={!bean} /></Field>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}><Field label="Low stock threshold (g)"><input className="inp f-mono" type="number" min="0" value={f.lowStockThreshold} onChange={e => set('lowStockThreshold', e.target.value)} /></Field></motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Flavor tags"><div className="tag-row">{FLAVOR_TAGS.map(t => (
            <Chip key={t} active={f.flavorTags.includes(t)} onClick={() => set('flavorTags', f.flavorTags.includes(t) ? f.flavorTags.filter(x => x !== t) : [...f.flavorTags, t])}>{t}</Chip>
          ))}</div></Field>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}><Field label="Tasting notes"><textarea className="inp" rows={2} value={f.tastingNotes} onChange={e => set('tastingNotes', e.target.value)} placeholder="Blueberries, jasmine, a hint of madness…" /></Field></motion.div>
        <motion.div variants={stagger} initial="hidden" animate="show" custom={fi++}>
          <Field label="Photo">
            <div className="photo-row">
              <button type="button" className="btn" onClick={() => camRef.current?.click()}><Ic.camera size={15} /> 📷 Capture</button>
              <button type="button" className="btn" onClick={() => fileRef.current?.click()}><Ic.upload size={15} /> 📁 Upload</button>
              {f.photo && <button type="button" className="btn btn-ghost" onClick={() => set('photo', null)}><Ic.x size={14} /></button>}
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhotoFile} />
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhotoFile} />
            {f.photo && <img src={f.photo} alt="preview" className="photo-preview" />}
          </Field>
        </motion.div>
        <motion.button whileTap={{ scale: 0.95 }} className="btn btn-primary btn-block" onClick={save} variants={stagger} initial="hidden" animate="show" custom={fi++}><Ic.check size={15} /> {T('save')}</motion.button>
      </div>
    </Sheet>
  );
};

/* ============================================================
   RECIPES
   ============================================================ */
const Recipes = () => {
  const { data, nav, T, settings, ops, toast } = useApp();
  const q = settings.quirky;
  const [mFilter, setMFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const list = data.recipes.filter(r => mFilter === 'all' || r.method === mFilter).sort((a, b) => (b.isCurated - a.isCurated) || (new Date(b.createdAt) - new Date(a.createdAt)));
  return (
    <div className="page">
      <PageHead tag="— PROTOCOL LIBRARY" title="PROTOCOLS" sub={q ? `${data.recipes.length} extraction procedures. ${data.recipes.filter(r => r.isCurated).length} sanctioned.` : `${data.recipes.length} recipes total.`} />
      <div className="head-action-row">
        <div className="chip-row">
          <Chip active={mFilter === 'all'} onClick={() => setMFilter('all')}>All</Chip>
          {Object.entries(METHODS).map(([k, m]) => <Chip key={k} active={mFilter === k} onClick={() => { setMFilter(k); playClick(); }}>{m.label}</Chip>)}
        </div>
        <motion.button whileTap={{ scale: 0.93 }} className="btn btn-plasma btn-sm" onClick={() => { setEditing(null); setFormOpen(true); }}><Ic.plus size={14} /> {T('addRecipe')}</motion.button>
      </div>
      {list.length === 0 && <div className="card flat-card empty-mini">{T('emptyRecipes')}</div>}
      <div className="list-col">
        {list.map((r, i) => { const MI = METHOD_ICON[r.method]; return (
          <motion.div key={r.id} className="card proto-card proto-card-full" initial={{ opacity: 0, x: 26 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i }}>
            <div className="proto-main" onClick={() => { setEditing(r); setFormOpen(true); }}>
              <div className="proto-top"><span className="proto-mic" style={{ color: METHODS[r.method]?.color }}><MI size={17} /></span><span className="f-disp proto-name">{r.name}</span><Stamp kind={r.isCurated ? 'official' : 'exp'}>{r.isCurated ? 'OFFICIAL' : 'EXPERIMENT'}</Stamp></div>
              <div className="f-mono proto-stats">{r.coffeeDose}g · {r.waterAmount}ml · {r.temperature}°C · {fmtTime(recipeTotal(r))} · {r.grindSize}</div>
              {r.description && <div className="proto-desc">{r.description}</div>}
            </div>
            <motion.button whileTap={{ scale: 0.9 }} className="btn btn-primary btn-sm proto-brew" onClick={() => nav('brew', { recipeId: r.id })}><Ic.play size={13} /> BREW</motion.button>
          </motion.div>); })}
      </div>
      <RecipeForm open={formOpen} recipe={editing} onClose={() => setFormOpen(false)} onDelete={r => setDelTarget(r)} />
      <Confirm open={!!delTarget} danger title={q ? 'TERMINATE PROTOCOL?' : 'Delete recipe?'}
        body={delTarget ? `"${delTarget.name}" will be permanently removed from the library.` : ''} confirmLabel={T('del')}
        onYes={() => { ops.deleteRecipe(delTarget.id); toast(q ? 'PROTOCOL TERMINATED' : 'Recipe deleted', 'warn'); setDelTarget(null); setFormOpen(false); }}
        onNo={() => setDelTarget(null)} />
    </div>
  );
};

const RecipeForm = ({ open, recipe, onClose, onDelete }) => {
  const { ops, toast, T, settings } = useApp();
  const q = settings.quirky;
  const blank = { name: '', method: 'v60', coffeeDose: 15, waterAmount: 250, grindSize: 'Medium-Fine', temperature: 96, description: '', steps: [{ id: uid(), order: 0, kind: 'pour', description: 'Pour 50g to bloom.', duration: 30, targetWeight: 50, flowRate: 6, tip: '' }] };
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF(recipe ? JSON.parse(JSON.stringify({ ...blank, ...recipe })) : blank); }, [open, recipe]);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const setStep = (i, k, v) => setF(p => ({ ...p, steps: p.steps.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const move = (i, dir) => setF(p => { const arr = [...p.steps]; const j = i + dir; if (j < 0 || j >= arr.length) return p; [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...p, steps: arr.map((s, k) => ({ ...s, order: k })) }; });
  const save = () => {
    if (!f.name.trim()) { toast('Protocol needs a name.', 'warn'); return; }
    const water = Number(f.waterAmount) || 0;
    const method = f.method;
    const steps = f.steps.filter(s => s.description.trim() && Number(s.duration) > 0)
      .map((s, i) => ({
        ...s,
        order: i,
        duration: Number(s.duration),
        targetWeight: s.targetWeight === '' || s.targetWeight == null ? null : Number(s.targetWeight),
        flowRate: s.flowRate === '' || s.flowRate == null ? null : Number(s.flowRate),
        tip: s.tip?.trim() || null,
      }));
    if (steps.length === 0) { toast('A protocol requires at least one step.', 'warn'); return; }
    /* Derive targetWeightPct and stamp flowRate. Pour/plunge steps get a
       flowRate (default from method if user didn't set one); wait/stir steps
       have flowRate: null (they're time-bound, not volume-bound). */
    const stepsWithMeta = steps.map(s => {
      const isVol = s.kind === 'pour' || s.kind === 'plunge';
      let out = { ...s, targetWeightPct: (isVol && s.targetWeight != null && water > 0) ? Math.round((s.targetWeight / water) * 10000) / 10000 : null };
      if (isVol) {
        if (out.flowRate == null) {
          out.flowRate = s.kind === 'plunge' ? PLUNGE_FLOW_RATE : (METHODS[method]?.defaultFlowRate || 6);
        }
      } else {
        out.flowRate = null;
      }
      return out;
    });
    ops.saveRecipe({ ...f, name: f.name.trim(), steps: stepsWithMeta, coffeeDose: Number(f.coffeeDose) || 0, waterAmount: water, temperature: Number(f.temperature) || 0 }, recipe?.id);
    toast(q ? 'PROTOCOL COMMITTED' : 'Recipe saved'); onClose();
  };
  const duplicate = () => {
    const copy = JSON.parse(JSON.stringify(f)); copy.name = f.name + ' Copy'; copy.isCurated = false;
    copy.steps = copy.steps.map(s => ({ ...s, id: uid() }));
    /* Re-derive pcts on the duplicate so the clone is scale-invariant too. */
    const w = Number(copy.waterAmount) || 0;
    const method = copy.method;
    copy.steps = copy.steps.map(s => {
      const isVol = s.kind === 'pour' || s.kind === 'plunge';
      let out = { ...s, targetWeightPct: (isVol && s.targetWeight != null && w > 0) ? Math.round((s.targetWeight / w) * 10000) / 10000 : null };
      if (isVol) {
        if (out.flowRate == null) {
          out.flowRate = s.kind === 'plunge' ? PLUNGE_FLOW_RATE : (METHODS[method]?.defaultFlowRate || 6);
        }
      } else {
        out.flowRate = null;
      }
      return out;
    });
    ops.saveRecipe(copy, null); toast(q ? 'PROTOCOL CLONED' : 'Recipe duplicated'); onClose();
  };
  return (
    <Sheet open={open} onClose={onClose} title={recipe ? (recipe.isCurated ? 'OFFICIAL PROTOCOL' : 'EDIT PROTOCOL') : T('addRecipe').toUpperCase()} tag={recipe ? `REF ${String(recipe.id).toUpperCase().slice(0, 8)}` : 'NEW PROTOCOL'}>
      <div className="form-col">
        <Field label="Name *"><input className="inp" value={f.name} onChange={e => set('name', e.target.value)} placeholder="Hoffmann V60" /></Field>
        <Field label="Method *"><div className="select-wrap"><select className="inp" value={f.method} onChange={e => set('method', e.target.value)}>{Object.entries(METHODS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select><Ic.chevD size={15} className="select-chev" /></div></Field>
        <div className="grid-2">
          <Field label="Dose (g) *"><input className="inp f-mono" type="number" step="0.5" value={f.coffeeDose} onChange={e => set('coffeeDose', e.target.value)} /></Field>
          <Field label="Water (ml) *"><input className="inp f-mono" type="number" value={f.waterAmount} onChange={e => set('waterAmount', e.target.value)} /></Field>
          <Field label="Grind *"><input className="inp" value={f.grindSize} onChange={e => set('grindSize', e.target.value)} /></Field>
          <Field label="Temp (°C) *"><input className="inp f-mono" type="number" value={f.temperature} onChange={e => set('temperature', e.target.value)} /></Field>
        </div>
        <Field label="Description"><textarea className="inp" rows={2} value={f.description} onChange={e => set('description', e.target.value)} placeholder="Classic pour-over with bloom" /></Field>
        <div className="steps-head">
          <span className="sec-tag">— PROCEDURE ({f.steps.length} STEPS · {fmtTime(f.steps.reduce((a, s) => a + (Number(s.duration) || 0), 0))})</span>
          <button className="btn btn-sm" onClick={() => set('steps', [...f.steps, { id: uid(), order: f.steps.length, kind: 'pour', description: '', duration: 30, targetWeight: null, flowRate: null, tip: '' }])}><Ic.plus size={13} /> Step</button>
        </div>
        <div className="steps-list">
          {f.steps.map((s, i) => (
            <motion.div key={s.id} className="step-row" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="step-row-top">
                <span className="step-num f-mono">{String(i + 1).padStart(2, '0')}</span>
                <div className="select-wrap step-sel"><select className="inp inp-sm" value={s.kind} onChange={e => {
                  const newKind = e.target.value;
                  /* When switching kind, reset fields that don't apply: pour/plunge
                     have targetWeight + flowRate; wait/stir have neither. */
                  setStep(i, 'kind', newKind);
                  if (newKind === 'pour' || newKind === 'plunge') {
                    if (s.targetWeight == null) setStep(i, 'targetWeight', '');
                    if (s.flowRate == null && newKind === 'plunge') setStep(i, 'flowRate', PLUNGE_FLOW_RATE);
                    else if (s.flowRate == null) setStep(i, 'flowRate', METHODS[f.method]?.defaultFlowRate || 6);
                  } else {
                    setStep(i, 'targetWeight', null);
                    setStep(i, 'flowRate', null);
                  }
                }}>{STEP_KINDS.map(k => <option key={k} value={k}>{(ACTION_META[k] || {}).label || k}</option>)}</select><Ic.chevD size={12} className="select-chev" /></div>
                <input className="inp inp-sm f-mono step-dur" type="number" min="1" value={s.duration} onChange={e => setStep(i, 'duration', e.target.value)} title="seconds" />
                <span className="dur-hint f-mono">{fmtTime(Number(s.duration) || 0)}</span>
                <span className="step-row-btns">
                  <button className="mini-btn" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><Ic.up size={13} /></button>
                  <button className="mini-btn" onClick={() => move(i, 1)} disabled={i === f.steps.length - 1} aria-label="Move down"><Ic.down size={13} /></button>
                  <button className="mini-btn mini-danger" onClick={() => set('steps', f.steps.filter((_, j) => j !== i))} aria-label="Delete step"><Ic.x size={13} /></button>
                </span>
              </div>
              <input className="inp inp-sm" placeholder="e.g. Pour 50g in slow circles" value={s.description} onChange={e => setStep(i, 'description', e.target.value)} />
              <div className="grid-2">
                <input className="inp inp-sm f-mono" type="number" placeholder="Target g (opt)" value={s.targetWeight ?? ''} onChange={e => setStep(i, 'targetWeight', e.target.value)} />
                <input className="inp inp-sm" placeholder="Tip (opt)" value={s.tip || ''} onChange={e => setStep(i, 'tip', e.target.value)} />
              </div>
              {(s.kind === 'pour' || s.kind === 'plunge') && (
                <div className="grid-2" style={{ marginTop: 6 }}>
                  <input className="inp inp-sm f-mono" type="number" step="0.1" placeholder="Flow rate (g/s)" value={s.flowRate ?? ''} onChange={e => setStep(i, 'flowRate', e.target.value)} />
                  <div className="flow-preview f-mono">
                    {s.targetWeight && s.flowRate ? `≈ ${Math.round(Number(s.targetWeight) / Number(s.flowRate))}s pour` : '—'}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
        <div className="sheet-actions">
          <motion.button whileTap={{ scale: 0.95 }} className="btn btn-primary" onClick={save}><Ic.check size={15} /> {T('save')}</motion.button>
          {recipe && <button className="btn" onClick={duplicate}><Ic.copy size={14} /> Duplicate</button>}
          {recipe && !recipe.isCurated && <button className="btn btn-danger" onClick={() => onDelete(recipe)}><Ic.trash size={14} /> {T('del')}</button>}
        </div>
        {recipe?.isCurated && <div className="curated-note f-mono">OFFICIAL PROTOCOL — deletion is forbidden by lab law.</div>}
      </div>
    </Sheet>
  );
};

/* ============================================================
   REPORTS
   ============================================================ */
const Reports = () => {
  const { data, route, settings } = useApp();
  const [tab, setTab] = useState(route.params?.tab === 'journal' ? 'journal' : 'analytics');
  const [detail, setDetail] = useState(null);
  useEffect(() => { if (route.params?.focus) { const b = data.brews.find(x => x.id === route.params.focus); if (b) { setTab('journal'); setDetail(b); } } }, []);
  return (
    <div className="page">
      <PageHead tag="— LAB OUTPUT" title="REPORTS" sub={settings.quirky ? 'The numbers do not lie. Unlike you, sometimes.' : 'Analytics and brew journal.'} />
      <div className="tab-row">
        {[['analytics', '📊 Analytics'], ['journal', '📜 Journal']].map(([k, label]) => (
          <button key={k} className={'tab-btn' + (tab === k ? ' tab-on' : '')} onClick={() => { setTab(k); playClick(); }}>{label}{tab === k && <span className="tab-ind" />}</button>
        ))}
      </div>
      {tab === 'analytics' ? <Analytics /> : <Journal onOpen={setDetail} />}
      <BrewDetail brew={detail} onClose={() => setDetail(null)} />
    </div>
  );
};

const Analytics = () => {
  const { data, T, resolvedTheme } = useApp();
  const dark = resolvedTheme === 'dark';
  const brews = data.brews;
  const rated = brews.filter(b => b.rating);
  const avg = rated.length ? rated.reduce((a, b) => a + b.rating, 0) / rated.length : 0;
  const methodCounts = {}; brews.forEach(b => methodCounts[b.method] = (methodCounts[b.method] || 0) + 1);
  const fav = Object.entries(methodCounts).sort((a, b) => b[1] - a[1])[0];
  const PIE_COLORS = ['#76FF03', '#FFC107', '#AA00FF', '#FF7043'];
  const c = {
    axis: dark ? '#9a8e84' : '#6e6259', grid: dark ? 'rgba(245,245,240,0.09)' : 'rgba(36,26,22,0.12)',
    line: dark ? '#76FF03' : '#4d9b00', dot: '#AA00FF', bar: dark ? '#AA00FF' : '#8a00cc',
    ink: dark ? '#F5F5F0' : '#241A16', track: dark ? 'rgba(245,245,240,0.10)' : 'rgba(36,26,22,0.10)',
    tipBg: dark ? '#241f1b' : '#ffffff', tipBd: dark ? '#F5F5F0' : '#241A16',
  };
  const trend = useMemo(() => {
    const byDay = {}; const cutoff = Date.now() - 30 * 864e5;
    brews.forEach(b => { const t = new Date(b.startTime).getTime(); if (t < cutoff || !b.rating) return; const k = dayLabel(b.startTime); (byDay[k] = byDay[k] || []).push(b.rating); });
    return Object.entries(byDay).map(([d, rs]) => ({ d, v: +(rs.reduce((a, b) => a + b, 0) / rs.length).toFixed(2) }));
  }, [brews]);
  const consumption = useMemo(() => {
    const out = [];
    for (let w = 3; w >= 0; w--) {
      const end = Date.now() - w * 7 * 864e5, start = end - 7 * 864e5;
      const g = brews.filter(b => { const t = new Date(b.startTime).getTime(); return t >= start && t < end; }).reduce((a, b) => a + (Number(b.coffeeDose) || 0), 0);
      out.push({ d: dayLabel(new Date(start + 864e5).toISOString()), g });
    }
    return out;
  }, [brews]);
  const pieData = Object.entries(methodCounts).map(([k, v], i) => ({ name: METHODS[k]?.label || k, value: v, key: k, color: PIE_COLORS[i % PIE_COLORS.length] }));
  const topBeans = useMemo(() => {
    const byBean = {};
    brews.forEach(b => { if (b.beanId && b.rating) (byBean[b.beanId] = byBean[b.beanId] || []).push(b.rating); });
    return Object.entries(byBean).map(([id, rs]) => ({ bean: data.beans.find(x => x.id === Number(id)), n: rs.length, avg: rs.reduce((a, b) => a + b, 0) / rs.length }))
      .filter(x => x.bean && x.n >= 2).sort((a, b) => b.avg - a.avg).slice(0, 5);
  }, [brews, data.beans]);
  if (brews.length === 0) return <div className="card flat-card empty-mini">{T('emptyBrews')}</div>;
  const FavIcon = fav ? METHOD_ICON[fav[0]] : Ic.flask;
  return (
    <div className="list-col">
      <div className="ana-kpis">
        <div className="card kpi"><div className="kpi-label f-mono">TOTAL BREWS</div><div className="kpi-val f-disp">{brews.length}</div></div>
        <div className="card kpi"><div className="kpi-label f-mono">FAVORITE METHOD</div><div className="kpi-fav">{fav ? <><FavIcon size={20} /> <b>{METHODS[fav[0]]?.label}</b> <span className="f-mono">×{fav[1]}</span></> : '—'}</div></div>
        <div className="card kpi"><div className="kpi-label f-mono">AVG RATING</div><div className="kpi-val f-disp">{rated.length ? avg.toFixed(1) : '—'}<span className="kpi-star">★</span></div><Stars value={avg} size={12} /></div>
      </div>
      <div className="card chart-card">
        <div className="sec-tag">— RATING TREND · 30 DAYS</div>
        {trend.length >= 2 ? <div className="chart-box"><LineChartSVG data={trend} c={c} /></div> : <div className="chart-empty f-mono">Need 2+ rated days for a trend line.</div>}
      </div>
      <div className="grid-2 chart-duo">
        <div className="card chart-card">
          <div className="sec-tag">— METHOD SPLIT</div>
          <div className="chart-box"><DonutSVG data={pieData} c={c} total={brews.length} /></div>
          <div className="legend">{pieData.map(p => <span key={p.key} className="legend-item f-mono"><i style={{ background: p.color }} />{p.name} ×{p.value}</span>)}</div>
        </div>
        <div className="card chart-card">
          <div className="sec-tag">— GRAMS / WEEK</div>
          <div className="chart-box"><BarChartSVG data={consumption} c={c} /></div>
        </div>
      </div>
      <div className="card chart-card">
        <div className="sec-tag">— TOP SPECIMENS (MIN 2 BREWS)</div>
        {topBeans.length === 0 ? <div className="chart-empty f-mono">Not enough rated data yet. Science takes time.</div> : (
          <div className="leaderboard">
            {topBeans.map((t, i) => (
              <motion.div key={t.bean.id} className="lb-row" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}>
                <span className="lb-rank">{['🥇', '🥈', '🥉'][i] || `#${i + 1}`}</span>
                <div className="lb-main"><b>{t.bean.name}</b><span className="f-mono lb-meta">{t.n} brews · {t.bean.roaster}</span><FreshnessStat bean={t.bean} compact /></div>
                <div className="lb-score f-mono">{t.avg.toFixed(1)}★</div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Journal = ({ onOpen }) => {
  const { data, T, toast } = useApp();
  const [mF, setMF] = useState('all'), [bF, setBF] = useState('all'), [rF, setRF] = useState(0);
  const beanName = id => data.beans.find(b => b.id === id)?.name || 'No specimen';
  const list = [...data.brews].sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
    .filter(b => (mF === 'all' || b.method === mF) && (bF === 'all' || String(b.beanId) === bF) && (!rF || (b.rating || 0) >= rF));
  const exportCsv = () => {
    const head = 'id,date,method,recipeName,beanName,coffeeDose,waterAmount,grindSize,temperature,rating,notes';
    const rows = data.brews.map(b => [b.id, new Date(b.startTime).toISOString(), b.method, b.recipeName, beanName(b.beanId), b.coffeeDose, b.waterAmount, b.grindSize, b.temperature, b.rating ?? '', b.notes ?? ''].map(csvCell).join(','));
    download('coffee-lab-brews.csv', [head, ...rows].join('\n'), 'text/csv'); toast('CSV EXPORTED — spreadsheet science!');
  };
  return (
    <div className="list-col">
      <div className="journal-bar">
        <div className="select-wrap sel-slim"><select className="inp inp-sm" value={mF} onChange={e => setMF(e.target.value)}><option value="all">All methods</option>{Object.entries(METHODS).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select><Ic.chevD size={12} className="select-chev" /></div>
        <div className="select-wrap sel-slim"><select className="inp inp-sm" value={bF} onChange={e => setBF(e.target.value)}><option value="all">All beans</option>{data.beans.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select><Ic.chevD size={12} className="select-chev" /></div>
        <div className="select-wrap sel-slim"><select className="inp inp-sm" value={rF} onChange={e => setRF(Number(e.target.value))}><option value={0}>Any ★</option><option value={3}>≥3★</option><option value={4}>≥4★</option><option value={5}>5★</option></select><Ic.chevD size={12} className="select-chev" /></div>
        <motion.button whileTap={{ scale: 0.93 }} className="btn btn-sm" onClick={exportCsv}><Ic.download size={13} /> CSV</motion.button>
      </div>
      {list.length === 0 ? <div className="card flat-card empty-mini">{T('emptyBrews')}</div> : list.map((b, i) => { const MI = METHOD_ICON[b.method] || Ic.flask; return (
        <motion.button key={b.id} className="card journal-row" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }} whileTap={{ scale: 0.98 }} onClick={() => onOpen(b)}>
          <div className="jr-left"><span className="jr-ic" style={{ color: METHODS[b.method]?.color }}><MI size={19} /></span>
            <div className="jr-main"><b>{b.recipeName}</b><span className="f-mono jr-sub">{beanName(b.beanId)} · {b.coffeeDose}g/{b.waterAmount}ml</span></div></div>
          <div className="jr-right"><Stars value={b.rating || 0} size={12} /><span className="f-mono jr-date">{new Date(b.startTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span></div>
        </motion.button>); })}
    </div>
  );
};

const BrewDetail = ({ brew, onClose }) => {
  const { data } = useApp();
  if (!brew) return <Sheet open={false} onClose={() => {}} title="" />;
  const bean = data.beans.find(b => b.id === brew.beanId);
  const MI = METHOD_ICON[brew.method] || Ic.flask;
  return (
    <Sheet open={!!brew} onClose={onClose} title={brew.recipeName} tag={`EXPERIMENT LOG · ${dayLabel(brew.startTime)}`}>
      <div className="bd-grid">
        <div className="bd-cell"><span className="dt">Method</span><span className="dd"><MI size={14} /> {METHODS[brew.method]?.label}</span></div>
        <div className="bd-cell"><span className="dt">Specimen</span><span className="dd">{bean?.name || 'No specimen'}</span></div>
        <div className="bd-cell"><span className="dt">Dose</span><span className="dd f-mono">{brew.coffeeDose}g</span></div>
        <div className="bd-cell"><span className="dt">Water</span><span className="dd f-mono">{brew.waterAmount}ml</span></div>
        <div className="bd-cell"><span className="dt">Grind</span><span className="dd">{brew.grindSize}</span></div>
        <div className="bd-cell"><span className="dt">Temp</span><span className="dd f-mono">{brew.temperature}°C</span></div>
        <div className="bd-cell"><span className="dt">Started</span><span className="dd f-mono">{new Date(brew.startTime).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></div>
        <div className="bd-cell"><span className="dt">Duration</span><span className="dd f-mono">{fmtTime((brew.stepsCompleted || []).reduce((a, s) => a + s.actualDuration, 0))}</span></div>
        <div className="bd-cell"><span className="dt">Rating</span><span className="dd">{brew.rating ? <Stars value={brew.rating} size={15} /> : '— unrated —'}</span></div>
      </div>
      {brew.stepsCompleted?.length > 0 && <>
        <div className="sec-tag">— STEP TELEMETRY</div>
        <table className="telemetry"><thead><tr><th>#</th><th>Planned</th><th>Actual</th><th>Δ</th></tr></thead><tbody>
          {brew.stepsCompleted.map((s, i) => { const delta = s.actualDuration - s.plannedDuration; return (
            <tr key={i}><td className="f-mono">{String(i + 1).padStart(2, '0')}</td><td className="f-mono">{fmtTime(s.plannedDuration)}</td><td className="f-mono">{fmtTime(s.actualDuration)}</td>
              <td className={'f-mono ' + (delta > 2 ? 'delta-over' : delta < -2 ? 'delta-under' : 'delta-ok')}>{delta > 0 ? '+' : ''}{delta}s</td></tr>); })}
        </tbody></table>
      </>}
      {brew.notes && <p className="tasting-notes">“{brew.notes}”</p>}
    </Sheet>
  );
};

/* ============================================================
   SETTINGS
   ============================================================ */
const Settings = () => {
  const { settings, setSettings, data, ops, toast, nav, T } = useApp();
  const q = settings.quirky;
  const fileRef = useRef(null);
  const [importData, setImportData] = useState(null);
  const [purgeArmed, setPurgeArmed] = useState(false);
  useEffect(() => { if (!purgeArmed) return; const t = setTimeout(() => setPurgeArmed(false), 4000); return () => clearTimeout(t); }, [purgeArmed]);
  const set = (k, v) => setSettings(s => ({ ...s, [k]: v }));
  const exportJson = () => {
    const payload = { version: '1.0', exportedAt: new Date().toISOString(), beans: data.beans, recipes: data.recipes, brews: data.brews };
    download('coffee-lab-data.json', JSON.stringify(payload, null, 2), 'application/json'); toast('LAB NOTES DOWNLOADED');
  };
  const onImportFile = e => {
    const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try { const d = JSON.parse(reader.result); if (!d || !d.version || !Array.isArray(d.beans) || !Array.isArray(d.recipes) || !Array.isArray(d.brews)) throw new Error('bad'); setImportData(d); }
      catch (err) { toast('Invalid lab data format. Check your file.', 'warn'); }
    };
    reader.readAsText(f);
  };
  return (
    <div className="page">
      <PageHead back={() => nav('dashboard', {})} tag="— SYSTEM" title={T('settings').toUpperCase()} sub={q ? 'Calibrate the apparatus.' : 'Preferences and data.'} />
      <div className="list-col">
        <div className="card set-card">
          <div className="sec-tag">— APPEARANCE</div>
          <div className="set-row"><span className="set-label">Theme</span>
            <div className="seg">{[['light', Ic.sun], ['system', Ic.monitor], ['dark', Ic.moon]].map(([k, I]) => (
              <button key={k} className={'seg-btn' + (settings.theme === k ? ' seg-on' : '')} onClick={() => { set('theme', k); playClick(); }}><I size={15} /></button>
            ))}</div></div>
          <div className="set-row"><span className="set-label">Quirky mode <em className="set-hint">{q ? 'mad scientist copy' : 'plain copy'}</em></span><Toggle on={settings.quirky} onChange={v => set('quirky', v)} /></div>
        </div>
        <div className="card set-card">
          <div className="sec-tag">— SOUND</div>
          <div className="set-row"><span className="set-label"><Ic.volume size={15} className="inline-ic" /> Sound effects</span><Toggle on={settings.sound} onChange={v => set('sound', v)} /></div>
          <div className="set-row"><span className="set-label">Volume <span className="f-mono set-hint">{settings.volume}</span></span>
            <input type="range" min="0" max="100" value={settings.volume} className="vol-slider" onChange={e => set('volume', Number(e.target.value))} onMouseUp={() => playBeep(660, 0.12)} onTouchEnd={() => playBeep(660, 0.12)} /></div>
        </div>
        <div className="card set-card">
          <div className="sec-tag">— DATA MANAGEMENT</div>
          <div className="data-actions">
            <button className="btn" onClick={exportJson}><Ic.download size={15} /> {T('export')} (.json)</button>
            <button className="btn" onClick={() => fileRef.current?.click()}><Ic.upload size={15} /> {T('import')} (.json)</button>
            <input ref={fileRef} type="file" accept="application/json,.json" hidden onChange={onImportFile} />
            <motion.button whileTap={{ scale: 0.95 }} className={'btn btn-danger' + (purgeArmed ? ' purge-armed' : '')}
              onClick={() => { if (!purgeArmed) { setPurgeArmed(true); return; } ops.purgeAll(); setPurgeArmed(false); toast(q ? 'LAB STERILIZED. FRESH START.' : 'All data purged.', 'warn'); }}>
              <Ic.trash size={15} /> {purgeArmed ? 'TAP AGAIN TO CONFIRM' : '⚠️ PURGE ALL DATA'}
            </motion.button>
          </div>
        </div>
        <div className="card set-card about-card">
          <div className="sec-tag">— ABOUT</div>
          <div className="about-logo"><Ic.flask size={22} /> <span className="f-disp">COFFEE LAB</span> <span className="f-mono about-ver">v1.0.0</span></div>
          <p className="about-line">Made with questionable science ☕🧪</p>
          <p className="f-mono about-fine">LOCAL-FIRST · NO ACCOUNTS · YOUR DATA NEVER LEAVES THIS DEVICE</p>
        </div>
      </div>
      <Confirm open={!!importData} title={q ? 'UPLOAD EXTERNAL DATA?' : 'Import data?'}
        body={importData ? `This will replace all current data. ${importData.beans.length} beans, ${importData.recipes.length} recipes, ${importData.brews.length} brews found. Proceed?` : ''}
        confirmLabel="REPLACE ALL" onYes={() => { ops.importAll(importData); setImportData(null); toast('LAB DATA UPLOADED'); }} onNo={() => setImportData(null)} />
    </div>
  );
};

/* ============================================================
   APP ROOT
   ============================================================ */
export default function App() {
  const [data, setData] = useState(loadData);
  const [settings, setSettings] = useState(loadSettings);
  const [route, setRoute] = useState({ page: 'dashboard', params: {} });
  const [toasts, setToasts] = useState([]);
  const [resolvedTheme, setResolvedTheme] = useState('dark');

  useEffect(() => { try { localStorage.setItem(DATA_KEY, JSON.stringify(data)); } catch (e) { /* */ } }, [data]);
  useEffect(() => { try { localStorage.setItem(SET_KEY, JSON.stringify(settings)); } catch (e) { /* */ } soundState.on = settings.sound; soundState.vol = settings.volume / 100; }, [settings]);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setResolvedTheme(settings.theme === 'system' ? (mq.matches ? 'dark' : 'light') : settings.theme);
    apply(); mq.addEventListener?.('change', apply); return () => mq.removeEventListener?.('change', apply);
  }, [settings.theme]);
  useEffect(() => { let m = document.querySelector('meta[name="theme-color"]'); if (!m) { m = document.createElement('meta'); m.name = 'theme-color'; document.head.appendChild(m); } m.content = '#3E2723'; }, []);

  const toast = useCallback((msg, kind = 'ok') => { setToasts(t => [...t.slice(-2), { id: uid(), msg, kind, ttl: 2800 }]); }, []);
  const nav = useCallback((page, params = {}) => { setRoute({ page, params }); window.scrollTo({ top: 0 }); }, []);
  const T = useCallback(key => { const c = COPY[key]; return c ? (settings.quirky ? c[1] : c[0]) : key; }, [settings.quirky]);

  const ops = useMemo(() => ({
    saveBean: (bean, id) => setData(d => id ? { ...d, beans: d.beans.map(b => b.id === id ? { ...bean, id } : b) } : { ...d, beans: [...d.beans, { ...bean, id: Date.now(), createdAt: new Date().toISOString() }] }),
    deleteBean: id => setData(d => ({ ...d, beans: d.beans.filter(b => b.id !== id), brews: d.brews.filter(b => b.beanId !== id) })),
    saveRecipe: (r, id) => setData(d => id ? { ...d, recipes: d.recipes.map(x => x.id === id ? { ...r, id } : x) } : { ...d, recipes: [...d.recipes, { ...r, id: uid(), isCurated: false, createdAt: new Date().toISOString() }] }),
    deleteRecipe: id => setData(d => ({ ...d, recipes: d.recipes.filter(r => r.id !== id) })),
    addBrew: brew => {
      let beanEmptied = false, beanName = null;
      setData(d => {
        const beans = d.beans.map(b => {
          if (b.id !== brew.beanId) return b;
          const nw = Math.max(0, b.currentWeight - (Number(brew.coffeeDose) || 0));
          if (nw === 0 && b.currentWeight > 0) { beanEmptied = true; beanName = b.name; }
          return { ...b, currentWeight: nw };
        });
        return { ...d, beans, brews: [...d.brews, brew] };
      });
      return { beanEmptied, beanName };
    },
    purgeAll: () => setData(seedData()),
    importAll: d => setData({ beans: d.beans, recipes: d.recipes, brews: d.brews }),
  }), []);

  const ctx = { data, settings, setSettings, route, nav, toast, toasts, setToasts, T, ops, resolvedTheme };
  const PAGES = { dashboard: <Dashboard />, brew: <BrewFlow />, beans: <Beans />, recipes: <Recipes />, reports: <Reports />, settings: <Settings /> };

  return (
    <AppCtx.Provider value={ctx}>
      <div className="cl-root" data-theme={resolvedTheme}>
        <Floaters />
        <main className="cl-main">
          <div key={route.page} className="fx-page">
            {PAGES[route.page] || <Dashboard />}
          </div>
        </main>
        <BottomNav />
        <InstallFab />
        <ToastStack />
      </div>
    </AppCtx.Provider>
  );
}
